import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as pdfjs from 'pdfjs-dist'
import type { PDFDocumentProxy } from 'pdfjs-dist'

import {
  ApiError,
  type Bookmark,
  type CompareResult,
  type FieldBuildKind,
  type FieldSuggestion,
  type FormField,
  type PageScale,
  type StructureBlock,
  type Paragraph,
  type SignatureReport,
  type PdfLink,
  api,
  type CommentKind,
  type EditReport,
  type FindMatch,
  type GlyphAddition,
  type StampKind,
  type Probe,
  type PageInfo,
  type PageModel,
  type PlacedObject,
  type ArrangeAction,
  type CommentThread,
  type RedactionRegion,
  type Run,
  type SessionInfo,
} from './api'
import { Ribbon, type Tab } from './ribbon/Ribbon'
import { PageView } from './viewer/PageView'
import { Thumbnails } from './viewer/Thumbnails'
import { SignatureDialog, type SignatureResult } from './dialogs/SignatureDialog'
import { RedactionPanel } from './dialogs/RedactionPanel'
import { SplitDialog } from './dialogs/SplitDialog'
import { CommentsPanel } from './dialogs/CommentsPanel'
import { PasswordDialog } from './dialogs/PasswordDialog'
import { ExportImagesDialog } from './dialogs/ExportImagesDialog'
import { CREATE_ACCEPT, createPdf, isOfficeName } from './files/createPdf'
import { CompressDialog } from './dialogs/CompressDialog'
import { CertificateSignDialog } from './dialogs/CertificateSignDialog'
import { RewrapDialog } from './dialogs/RewrapDialog'
import { SaveChangesDialog } from './dialogs/SaveChangesDialog'
import { SignaturesDialog } from './dialogs/SignaturesDialog'
import { AskPanel } from './viewer/AskPanel'
import { AccessibilityPanel } from './viewer/AccessibilityPanel'
import { FieldPanel } from './viewer/FieldPanel'
import { ScaleDialog } from './dialogs/ScaleDialog'
import type { MeasureTool } from './viewer/MeasureLayer'
import { CompareView } from './viewer/CompareView'
import { leavingAllowed } from './state/leave'
import { LinkDialog } from './dialogs/LinkDialog'
import { OneDriveDialog } from './dialogs/OneDriveDialog'
import { BookmarksPanel } from './viewer/BookmarksPanel'
import { ChangedSinceOpened, oneDrive, type DriveItem, type DriveLocation } from './files/oneDrive'
import { graphAvailable } from './auth/msal'
import { ProtectDialog } from './dialogs/ProtectDialog'
import { MarksDialog } from './dialogs/MarksDialog'
import { StampDialog } from './dialogs/StampDialog'
import { FindBar } from './viewer/FindBar'
import type { MarkupGeometry } from './viewer/PageView'
import { currentAccount } from './auth/msal'
import {
  capabilities,
  onFileLaunch,
  pickFile,
  saveCopy,
  saveInPlace,
  type OpenedFile,
} from './files/fileAccess'
import { registerOpenDocument, type OpenDocument } from './auth/carry'
import {
  findDefaultLocalFont,
  findLocalFont,
  localFontPermission,
  localFontsSupported,
} from './files/localFonts'

// Bundle the worker rather than fetching it from a CDN: the app must work on
// an internal network with no outbound internet access.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString()

type Notice = { level: 'info' | 'warn' | 'fail'; title: string; body: string } | null

type FidelityLevel = 'exact' | 'warn' | 'fail'

const EXACT_TEXT = 'Fidelity: exact \u2014 original fonts preserved'

function letters(chars: string[]): string {
  return chars.map((c) => (c === ' ' ? 'space' : c)).join(' ')
}

function sourcesOf(additions: GlyphAddition[]): string {
  return [...new Set(additions.map((a) => a.source))].join('; ')
}

/**
 * What an edit's glyph plan means, in the status bar's words. Letters added
 * from the document's own font are still exact; only a lookalike is not.
 */
function describeFidelity(
  plan: Pick<Probe, 'fidelity' | 'additions' | 'unresolved'>,
  done: boolean,
): { level: FidelityLevel; text: string } {
  const added = plan.additions.map((a) => a.char)
  switch (plan.fidelity) {
    case 'exact':
      return { level: 'exact', text: EXACT_TEXT }
    case 'extended':
      return {
        level: 'exact',
        text: `Fidelity: exact \u2014 ${done ? 'added' : 'adding'} ${letters(added)} from ${sourcesOf(plan.additions)}`,
      }
    case 'substituted': {
      const close = plan.additions.filter((a) => !a.exact)
      return {
        level: 'warn',
        text: `Fidelity: close match \u2014 ${letters(close.map((a) => a.char))} from ${sourcesOf(close)}`,
      }
    }
    default:
      return {
        level: 'fail',
        text: `Fidelity: blocked \u2014 no available font can draw ${letters(plan.unresolved)}`,
      }
  }
}

/** What a click on the page canvas will do. */
export type Tool =
  | 'select'
  | 'text'
  | 'date'
  | 'image'
  | 'signature'
  | 'paragraph'
  | 'redact'
  | 'whiteout'
  | 'crop'
  | 'note'
  | 'highlight'
  | 'underline'
  | 'strikeout'
  | 'pen'
  | 'rectangle'
  | 'ellipse'
  | 'line'
  | 'arrow'
  | 'stamp'
  | 'link'
  | 'sigbox'
  | 'measure-distance'
  | 'measure-perimeter'
  | 'measure-area'
  | 'calibrate'
  | 'prepare'
  | 'field-text'
  | 'field-checkbox'
  | 'field-radio'
  | 'field-combo'
  | 'field-list'
  | 'field-signature'

/** Tools that set new text in the session's default font. */
const NEW_TEXT_TOOLS = new Set<Tool>(['text', 'date', 'stamp'])

/** A stand-in page model from the page list, until the real one arrives:
 * the right size, no runs yet. */
function placeholderModel(info: PageInfo | undefined, index: number): PageModel {
  const rotation = info?.rotation ?? 0
  const turned = rotation === 90 || rotation === 270
  return {
    pageIndex: index,
    version: '',
    width: (turned ? info?.height : info?.width) ?? 612,
    height: (turned ? info?.width : info?.height) ?? 792,
    originX: 0,
    originY: 0,
    rotation,
    runs: [],
  }
}

/** The zoom at which a page fits the document view. */
function fitScale(info: PageInfo | undefined): number {
  const view = document.querySelector('.canvas') as HTMLElement | null
  if (!info || !view) return 1.25
  const room = { w: view.clientWidth - 48, h: view.clientHeight - 48 }
  const fit = Math.min(room.w / info.width, room.h / info.height)
  return Math.max(0.02, Math.min(fit, 5))
}

export default function App({ resume = null }: { resume?: OpenDocument | null }) {
  const [session, setSession] = useState<SessionInfo | null>(null)
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null)
  const [pages, setPages] = useState<PageModel[]>([])
  const [pageInfo, setPageInfo] = useState<PageInfo[]>([])
  const [objects, setObjects] = useState<PlacedObject[]>([])
  const [scale, setScale] = useState(1.25)
  const [tool, setTool] = useState<Tool>('select')
  const [current, setCurrent] = useState(0)
  const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set())
  const [selectedObject, setSelectedObject] = useState<string | null>(null)
  // The run being edited: its page, and its place on the page (see
  // runKey) rather than its instruction index. One number for the whole
  // document opened the same-numbered run on every page, and typing went to
  // the last of them; an index also shifts under an edit elsewhere on the page.
  const [activeRun, setActiveRun] = useState<{ pageIndex: number; key: string } | null>(null)
  // Page models by version, across refreshes (see refresh).
  const modelCache = useRef(new Map<string, PageModel>())
  const refreshGeneration = useRef(0)
  const currentRef = useRef(0)
  // How much of each page is in view, for the current page (see onVisibility).
  const visibility = useRef(new Map<number, number>())
  const visibilityFrame = useRef(0)
  const [notice, setNotice] = useState<Notice>(null)
  const [fidelity, setFidelity] = useState<FidelityLevel>('exact')
  const [fidelityText, setFidelityText] = useState(EXACT_TEXT)
  /** The last probe, so commit can act on it without asking again. */
  const lastProbe = useRef<{ key: string; probe: Probe } | null>(null)
  const [busy, setBusy] = useState(false)
  const [signatureOpen, setSignatureOpen] = useState(false)
  const [ocrPages, setOcrPages] = useState<Set<number>>(new Set())
  const [pendingPlace, setPendingPlace] = useState<{ pageIndex: number; x: number; y: number } | null>(null)
  const [foundRegions, setFoundRegions] = useState<RedactionRegion[]>([])
  const [redactPanelOpen, setRedactPanelOpen] = useState(false)
  const [splitOpen, setSplitOpen] = useState(false)
  // Destructive crop removes content, so it is confirmed rather than
  // applied on the drag like a view crop.
  const [cropDestructive, setCropDestructive] = useState(false)
  const [passwordFor, setPasswordFor] = useState<{ file: OpenedFile; wrong: boolean } | null>(null)
  // Forms, links and bookmarks of the open document.
  const [formFields, setFormFields] = useState<FormField[]>([])
  const [highlightFields, setHighlightFields] = useState(true)
  const [links, setLinks] = useState<PdfLink[]>([])
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([])
  const [sidePanel, setSidePanel] = useState<'pages' | 'bookmarks'>('pages')
  const [compressOpen, setCompressOpen] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)
  const [linkArea, setLinkArea] = useState<{ pageIndex: number; rect: [number, number, number, number] } | null>(null)
  const [oneDriveMode, setOneDriveMode] = useState<'open' | 'save' | 'compare' | null>(null)
  // Unsaved changes: the revision last saved (null until a document is open).
  const [savedRevision, setSavedRevision] = useState<number | null>(null)
  const [pendingLeave, setPendingLeave] = useState<{ then: 'close' | 'open'; proceed: () => void } | null>(null)
  const [savingForLeave, setSavingForLeave] = useState(false)
  const [editParagraph, setEditParagraph] = useState<{ pageIndex: number; paragraph: Paragraph } | null>(null)
  const [rewrapping, setRewrapping] = useState(false)
  const [signReport, setSignReport] = useState<SignatureReport | null>(null)
  const [signOpen, setSignOpen] = useState(false)
  const [sigBox, setSigBox] = useState<{ pageIndex: number; rect: [number, number, number, number] } | null>(null)
  const [signaturesOpen, setSignaturesOpen] = useState(false)
  const [compare, setCompare] = useState<{
    result: CompareResult; other: import('pdfjs-dist').PDFDocumentProxy
    otherName: string; blob: Blob; password: string
  } | null>(null)
  const [comparePassword, setComparePassword] = useState<{ blob: Blob; name: string; wrong: boolean } | null>(null)
  const [askOpen, setAskOpen] = useState(false)
  // Measuring
  const [scales, setScales] = useState<PageScale[][]>([])
  const [scaleDialog, setScaleDialog] = useState<{ pageIndex: number; calibration: number[] | null } | null>(null)
  // Preparing a form
  const [selectedField, setSelectedField] = useState<string | null>(null)
  const [suggestions, setSuggestions] = useState<Record<number, FieldSuggestion[]>>({})
  // Accessibility
  const [accessOpen, setAccessOpen] = useState(false)
  const [orderBlocks, setOrderBlocks] = useState<StructureBlock[]>([])
  const [selectedBlock, setSelectedBlock] = useState<string | null>(null)
  // Combining a protected file: the files, the passwords so far, and which one is asked for.
  const [mergePassword, setMergePassword] = useState<{
    files: File[]; passwords: string[]; index: number; wrong: boolean; permissions: boolean
  } | null>(null)
  const [protectOpen, setProtectOpen] = useState(false)
  const [marksOpen, setMarksOpen] = useState(false)
  const [stampAt, setStampAt] = useState<{ pageIndex: number; x: number; y: number } | null>(null)
  const [find, setFind] = useState<{ replace: boolean } | null>(null)
  const [findMatches, setFindMatches] = useState<FindMatch[]>([])
  const [findCurrent, setFindCurrent] = useState(0)
  const [threads, setThreads] = useState<CommentThread[]>([])
  const [commentsOpen, setCommentsOpen] = useState(false)
  const [activeComment, setActiveComment] = useState<string | null>(null)

  currentRef.current = current
  const pagesRef = useRef<PageModel[]>([])
  pagesRef.current = pages

  const openedFile = useRef<OpenedFile | null>(null)
  const mergeInput = useRef<HTMLInputElement>(null)
  const createInput = useRef<HTMLInputElement>(null)
  const imageInput = useRef<HTMLInputElement>(null)

  const say = useCallback(
    (level: 'info' | 'warn' | 'fail', title: string, body: string) => {
      setNotice({ level, title, body })
      window.setTimeout(() => setNotice(null), 8000)
    },
    [],
  )

  // New text is set in the user's own Calibri when their computer has it,
  // not the server's stand-in. The browser hands the font over once allowed;
  // until then, the first action that sets new text asks.
  const fontHandedTo = useRef(new Map<string, Promise<void>>())
  const fontAsked = useRef(false)
  const handOverDefaultFont = useCallback(
    (sessionId: string, ask: boolean): Promise<void> => {
      const pending = fontHandedTo.current.get(sessionId)
      if (pending) return pending
      const work = (async () => {
        const permission = await localFontPermission()
        if (permission === 'unsupported' || permission === 'denied') return
        if (permission === 'prompt' && (!ask || fontAsked.current)) {
          fontHandedTo.current.delete(sessionId)    // ask when there is a click
          return
        }
        fontAsked.current = true
        const font = await findDefaultLocalFont()
        if (!font) return
        try {
          const r = await api.uploadFont(sessionId, font, '', 'default')
          if (permission === 'prompt') {
            say('info', `Using ${r.defaultFont ?? 'Calibri'} from this computer`,
              'New text, stamps and page marks will be set in it.')
          }
        } catch {
          // Refused (a licence that forbids embedding); the server's default stays.
        }
      })()
      fontHandedTo.current.set(sessionId, work)
      return work
    },
    [say],
  )

  /** Reload the rendered document, its text model, pages and objects. */
  const refresh = useCallback(async (info: SessionInfo) => {
    const generation = ++refreshGeneration.current
    const blob = await api.file(info.id)
    const data = await blob.arrayBuffer()
    const loaded = await pdfjs.getDocument({ data }).promise

    // Page models are fetched only when their version changed: an edit on
    // one page of 300 used to re-fetch (and the server rebuild) all 300.
    const [versions, list, objs, comments, form, linkList, outline] = await Promise.all([
      api.pageVersions(info.id).then((r) => r.versions),
      api.pageList(info.id),
      api.objects(info.id),
      api.comments(info.id),
      api.form(info.id),
      api.links(info.id),
      api.outline(info.id),
    ])
    void api.scales(info.id).then((r) => setScales(r.pages.map((p) => p.scales))).catch(() => setScales([]))
    // Checking signatures is slower; it need not hold up the page.
    void api.signatures(info.id).then(setSignReport).catch(() => setSignReport(null))

    const cache = modelCache.current
    // Until a changed page's new model arrives, it keeps showing its old one
    // (when the pages did not move): a placeholder would unmount every text
    // box on it, and lose what the user is typing in the next line.
    const shown = pagesRef.current
    const same = shown.length === versions.length
    const models = versions.map((version, i) =>
      cache.get(version) ?? (same && shown[i]?.version ? shown[i] : placeholderModel(list.pages[i], i)))
    const missing = versions.map((version, i) => ({ version, i })).filter(({ version }) => !cache.has(version))
    // Nearest the page being looked at first, so it becomes editable soonest.
    missing.sort((a, b) => Math.abs(a.i - currentRef.current) - Math.abs(b.i - currentRef.current))

    setDoc((previous) => {
      void previous?.destroy()
      return loaded
    })
    setPages(models)
    setPageInfo(list.pages)
    setObjects(objs.objects)
    setThreads(comments.threads)
    setFormFields(form.fields)
    setLinks(linkList.links)
    setBookmarks(outline.bookmarks)
    setSession(info)

    // Fetch what is missing in small batches; the first (around the current
    // page) before returning, the rest behind it. A newer refresh supersedes
    // this one, but what arrives is still cached by version.
    const batch = async (items: { version: string; i: number }[]) => {
      const fetched = await Promise.all(items.map(({ i }) => api.page(info.id, i).catch(() => null)))
      fetched.forEach((model) => model?.version && cache.set(model.version, model))
      if (generation !== refreshGeneration.current) return
      setPages((previous) => {
        const next = previous.slice()
        fetched.forEach((model, k) => {
          if (model && next.length > items[k].i) next[items[k].i] = model
        })
        return next
      })
    }
    const first = missing.slice(0, 6)
    const rest = missing.slice(6)
    await batch(first)
    void (async () => {
      for (let start = 0; start < rest.length; start += 12) {
        if (generation !== refreshGeneration.current) return
        await batch(rest.slice(start, start + 12))
      }
      if (cache.size > 3000) {
        // Bounded: keep what this document uses now.
        const keep = new Set(versions)
        for (const key of [...cache.keys()]) if (!keep.has(key)) cache.delete(key)
      }
    })()
  }, [])

  /** The current page follows scrolling: the page most in view. */
  const onVisibility = useCallback((pageIndex: number, ratio: number) => {
    visibility.current.set(pageIndex, ratio)
    if (visibilityFrame.current) return
    visibilityFrame.current = window.requestAnimationFrame(() => {
      visibilityFrame.current = 0
      let best = -1
      let most = 0
      for (const [index, amount] of visibility.current) {
        if (amount > most || (amount === most && amount > 0 && index < best)) {
          best = index
          most = amount
        }
      }
      if (best >= 0 && best !== currentRef.current) setCurrent(best)
    })
  }, [])

  const openFile = useCallback(
    async (file: OpenedFile, password = '') => {
      setBusy(true)
      setCompare(null)
      try {
        openedFile.current = file
        let info: SessionInfo
        try {
          info = await api.open(file, password)
        } catch (err) {
          const code = err instanceof ApiError ? (err.detail as { code?: string })?.code : undefined
          if (code === 'password_required' || code === 'password_incorrect') {
            setPasswordFor({ file, wrong: code === 'password_incorrect' })
            return
          }
          throw err
        }
        setPasswordFor(null)
        modelCache.current.clear()
        visibility.current.clear()
        currentRef.current = 0
        // A new document starts with Select: a destructive tool (Redact)
        // left armed would destroy content on the first drag.
        setTool('select')
        setActiveRun(null)
        await refresh(info)
        setSavedRevision(info.revision)
        void handOverDefaultFont(info.id, false)
        setFidelity('exact')
        setFidelityText(EXACT_TEXT)
        setCurrent(0)
        setSelectedPages(new Set())
        setOcrPages(new Set())

        // Tell the user up front if the document is a scan.
        const status = await api.ocrStatus(info.id)
        if (status.scannedPages.length) {
          const geometry = status.engines.find(
            (e) => e.available && e.suppliesGeometry,
          )
          say(
            'warn',
            'Scanned pages detected',
            geometry
              ? `${status.scannedPages.length} page(s) have no text. Run OCR from the page panel to make them searchable (${geometry.name}).`
              : `${status.scannedPages.length} page(s) have no text, and no OCR engine is available: ` +
                status.engines
                  .filter((e) => e.suppliesGeometry)
                  .map((e) => `${e.name} \u2014 ${e.reason}`)
                  .join('; '),
          )
        }
      } catch (err) {
        say('fail', 'Could not open', err instanceof Error ? err.message : String(err))
      } finally {
        setBusy(false)
      }
    },
    [refresh, say],
  )


  // Tell the auth layer what is open, so an expired sign-in can carry it
  // across the redirect to Microsoft and back.
  useEffect(() => {
    registerOpenDocument(() =>
      session ? { sessionId: session.id, file: openedFile.current, savedRevision } : null,
    )
  }, [session, savedRevision])

  // Back from re-authenticating: reattach to the server session rather than
  // making the user reopen the file. The session outlives the redirect by a
  // wide margin (ODIE_SESSION_TTL_MIN), so this is the normal case.
  const resumed = useRef(false)
  useEffect(() => {
    if (!resume || resumed.current) return
    resumed.current = true
    void (async () => {
      setBusy(true)
      try {
        const info = await api.describe(resume.sessionId)
        openedFile.current = resume.file
        await refresh(info)
        setSavedRevision(resume.savedRevision ?? info.revision)
        void handOverDefaultFont(info.id, false)
        say('info', 'Signed in again', `${info.filename} is open where you left it.`)
      } catch {
        say(
          'warn',
          'Document closed',
          'The editing session ended while you were signing in. Reopen the file to carry on.',
        )
      } finally {
        setBusy(false)
      }
    })()
  }, [resume, refresh, say])

  const run = useCallback(
    async <T,>(label: string, work: () => Promise<T>): Promise<T | null> => {
      setBusy(true)
      try {
        return await work()
      } catch (err) {
        const message =
          err instanceof ApiError && typeof err.detail === 'string'
            ? err.detail
            : err instanceof Error
              ? err.message
              : String(err)
        say('fail', label, message)
        return null
      } finally {
        setBusy(false)
      }
    },
    [say],
  )

  // --- file -----------------------------------------------------------------

  // --- unsaved changes ------------------------------------------------------

  const dirty = Boolean(session) && savedRevision !== null && session!.revision !== savedRevision

  /** Run ``proceed`` now, or after asking about unsaved changes. */
  const guardUnsaved = useCallback(
    (then: 'close' | 'open', proceed: () => void) => {
      if (!dirty) proceed()
      else setPendingLeave({ then, proceed })
    },
    [dirty],
  )

  const closeDocument = useCallback(() => {
    const id = session?.id
    if (id) void api.close(id).catch(() => undefined)
    openedFile.current = null
    setSession(null)
    setDoc((previous) => {
      void previous?.destroy()
      return null
    })
    setPages([])
    setPageInfo([])
    setObjects([])
    setThreads([])
    setFormFields([])
    setLinks([])
    setBookmarks([])
    setSignReport(null)
    setSavedRevision(null)
    setFind(null)
    setFindMatches([])
    setCompare(null)
    setAskOpen(false)
    setSelectedPages(new Set())
    setCurrent(0)
    setTool('select')
  }, [session])

  // Closing the tab or window with unsaved work: the browser's own prompt
  // (it allows no custom text). A sign-in redirect is leaving on purpose.
  useEffect(() => {
    if (!dirty) return
    const warn = (event: BeforeUnloadEvent) => {
      if (leavingAllowed()) return
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  useEffect(() => {
    document.title = session ? `${dirty ? '\u2022 ' : ''}${session.filename} \u2014 Odie` : 'Odie'
  }, [session, dirty])

  // A PDF double-clicked in Explorer while one is open: ask about unsaved
  // changes first, as for Open.
  useEffect(() => {
    onFileLaunch((file) => guardUnsaved('open', () => void openFile(file)))
  }, [openFile, guardUnsaved])

  const handleOpen = useCallback(async () => {
    // Pick first, while the click still counts as a user gesture (the file
    // picker needs one); then ask about unsaved changes before opening it.
    const file = await pickFile()
    if (file) guardUnsaved('open', () => void openFile(file))
  }, [openFile, guardUnsaved])

  /**
   * The bytes to write when saving, or null if saving was refused.
   *
   * One path for Save and Save As alike. Save As used to fetch the plain
   * document, so a redacted file saved that way skipped flattening -- and
   * the redacted text stayed recoverable in the copy. A redacted document is
   * never written as an ordinary revision: the flattened endpoint sanitises
   * metadata, rebuilds every object stream, and refuses to release the file
   * if any redacted value is still recoverable. Password protection is
   * applied either way.
   */
  const bytesForSaving = useCallback(async (): Promise<Blob | null> => {
    if (!session) return null
    const flatten = Boolean(session.redacted)
    try {
      return flatten ? await api.fileFlattened(session.id) : await api.fileForSave(session.id)
    } catch (err) {
      if (err instanceof ApiError && flatten) {
        const detail = err.detail as { message?: string; leaked?: string[] }
        say(
          'fail',
          'Save refused',
          `${detail?.message ?? 'Redaction verification failed.'} Still ` +
            `recoverable: ${(detail?.leaked ?? []).join(', ')}`,
        )
        return null
      }
      throw err
    }
  }, [session, say])

  /** False when the user, warned that saving breaks the signature, declines. */
  const signatureAllowsSave = useCallback((): boolean => {
    const signed = signReport?.changedSince ? signReport.signatures.length : 0
    return !signed || window.confirm(
      `This document is signed. Saving your changes will invalidate ${signed === 1 ? 'its signature' : `its ${signed} signatures`}.\n\n` +
      'Save anyway? (Cancel, then Undo, to get back to the signed version.)',
    )
  }, [signReport])

  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!session) return false
    if (!signatureAllowsSave()) return false
    const revision = session.revision
    const ok = await run('Save failed', async () => {
      const blob = await bytesForSaving()
      if (!blob) return false
      const removed = (blob as { sanitized?: string }).sanitized
      const how = session.redacted
        ? `flattened and verified${removed && removed !== 'nothing' ? ` (removed: ${removed})` : ''}`
        : 'saved'
      const guarded = session.protected ? ', with its password protection,' : ''
      const file = openedFile.current
      if (file?.source === 'onedrive' && file.driveItemId && file.driveId) {
        const target = { id: file.driveItemId, driveId: file.driveId }
        let saved
        try {
          saved = await oneDrive.replace(target, blob, file.eTag)
        } catch (err) {
          if (!(err instanceof ChangedSinceOpened)) throw err
          const replace = window.confirm(
            `${file.name} was changed by someone else since you opened it.\n\n` +
            'OK replaces their version with yours (OneDrive keeps theirs in Version history). ' +
            'Cancel leaves it alone, so you can use Save to OneDrive to keep yours as a copy.',
          )
          if (!replace) return false
          saved = await oneDrive.replace(target, blob)
        }
        openedFile.current = { ...file, eTag: saved.eTag, blob }
        say('info', 'Saved', `${session.filename} was ${how}${guarded} and saved to OneDrive.`)
        return true
      }
      const wrote = await saveInPlace(openedFile.current?.handle, blob)
      if (wrote) {
        say('info', 'Saved', `${session.filename} was ${how}${guarded} and written to disk.`)
        return true
      }
      if (await saveCopy(session.filename, blob)) {
        say('info', 'Saved a copy', `The original was not writable; the copy is ${how}.`)
        return true
      }
      return false
    })
    if (ok) setSavedRevision(revision)
    return Boolean(ok)
  }, [session, run, say, bytesForSaving, signatureAllowsSave])

  const handleSaveAs = useCallback(async () => {
    if (!session) return
    // The same warning as Save: the copy's signature breaks just the same.
    if (!signatureAllowsSave()) return
    const revision = session.revision
    const ok = await run('Save As failed', async () => {
      const blob = await bytesForSaving()
      return blob ? await saveCopy(session.filename, blob) : false
    })
    // The work is safe in the copy, so closing need not nag about it.
    if (ok) setSavedRevision(revision)
  }, [session, run, bytesForSaving, signatureAllowsSave])

  const saveToOneDrive = useCallback(
    async (folder: DriveLocation, name: string, overwrite: boolean): Promise<boolean> => {
      if (!session) return false
      const r = await run('Could not save to OneDrive', async () => {
        const blob = await bytesForSaving()
        if (!blob) return null
        const item = await oneDrive.create(folder, name, blob, overwrite)
        // From now on, Save goes to the new file, as after Save As anywhere.
        openedFile.current = {
          name: item.name, blob, source: 'onedrive',
          driveItemId: item.id, driveId: item.driveId, eTag: item.eTag,
        }
        return item
      })
      if (r) {
        setSavedRevision(session.revision)
        say('info', 'Saved to OneDrive', `${r.name} is in ${folder.name}.`)
      }
      return Boolean(r)
    },
    [session, run, bytesForSaving, say],
  )

  const openFromOneDrive = useCallback(
    async (item: DriveItem) => {
      setOneDriveMode(null)
      if (!item.isPdf) {
        // An Office file or a picture: open a PDF made from it. The original
        // stays as it is; Save makes a new PDF (Save to OneDrive puts it back).
        const made = await run(`Could not make a PDF from ${item.name}`, async () => {
          const stem = item.name.replace(/\.[^.]+$/, '')
          if (isOfficeName(item.name)) {
            say('info', 'Creating PDF', `Converting ${item.name} with Microsoft 365\u2026`)
            return { name: `${stem}.pdf`, blob: await oneDrive.officeAsPdf(item) }
          }
          const { blob } = await oneDrive.download(item)
          return await createPdf([new File([blob], item.name, { type: blob.type })])
        })
        if (made) await openFile({ name: made.name, blob: made.blob, source: 'local' })
        return
      }
      const r = await run('Could not open from OneDrive', () => oneDrive.download(item))
      if (!r) return
      await openFile({
        name: r.item.name, blob: r.blob, source: 'onedrive',
        driveItemId: r.item.id, driveId: r.item.driveId, eTag: r.item.eTag,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [run],
  )

  // --- navigation -------------------------------------------------------------

  /** Scroll to a page, and to a spot on it when ``top`` (PDF y) is given. */
  const goTo = useCallback(
    (pageIndex: number, top: number | null = null) => {
      setCurrent(pageIndex)
      const element = document.getElementById(`page-${pageIndex}`)
      if (!element) return
      element.scrollIntoView({ block: 'start' })
      const model = pages[pageIndex]
      const scroller = element.closest('.canvas')
      if (top !== null && model && !model.rotation && scroller) {
        const offset = (model.height - (top - model.originY)) * scale
        scroller.scrollBy({ top: Math.max(0, offset - 24) })
      }
    },
    [pages, scale],
  )

  const openExternal = useCallback(
    (uri: string) => {
      if (/^(https?:|mailto:|tel:)/i.test(uri)) window.open(uri, '_blank', 'noopener,noreferrer')
      else say('warn', 'Link not followed', `Odie only follows web, email and phone links: ${uri}`)
    },
    [say],
  )

  const followLink = useCallback(
    (link: PdfLink) => {
      if (link.uri) openExternal(link.uri)
      else if (link.targetPage !== null) goTo(link.targetPage, link.top)
    },
    [openExternal, goTo],
  )

  const addLink = useCallback(
    async (target: { uri: string } | { targetPage: number }) => {
      if (!session || !linkArea) return
      const area = linkArea
      setLinkArea(null)
      const r = await run('Could not add the link', () =>
        api.addLink(session.id, { pageIndex: area.pageIndex, rect: area.rect, ...target }))
      if (r) await reload(r.session)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session, linkArea, run],
  )

  const deleteLink = useCallback(
    async (id: string) => {
      if (!session) return
      const r = await run('Could not remove the link', () => api.deleteLink(session.id, id))
      if (r) await reload(r.session)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session, run],
  )

  const saveBookmarks = useCallback(
    async (next: Bookmark[]): Promise<boolean> => {
      if (!session) return false
      const r = await run('Could not change the bookmarks', () => api.setOutline(session.id, next))
      if (!r) return false
      setBookmarks(r.bookmarks)
      setSession(r.session)
      return true
    },
    [session, run],
  )

  // --- rewrap -------------------------------------------------------------------

  const applyRewrap = useCallback(
    async (text: string) => {
      if (!session || !editParagraph) return
      setRewrapping(true)
      const target = editParagraph
      const r = await run('Could not rewrap the paragraph', () =>
        api.rewrap(session.id, target.pageIndex, target.paragraph.runIndices, text))
      setRewrapping(false)
      if (!r) return
      setEditParagraph(null)
      setTool('select')
      await reload(r.session)
      const { report } = r
      const added = report.additions.map((a) => a.char).join(' ')
      if (report.warnings.length) say('warn', 'Paragraph rewrapped', report.warnings.join(' '))
      else say('info', 'Paragraph rewrapped',
        `${report.linesBefore} line(s) became ${report.linesAfter}, ${report.align === 'justify' ? 'justified' : report.align + '-aligned'}` +
        (added ? `. Added ${added} from the document\u2019s own font.` : '.'))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session, editParagraph, run, say],
  )

  // --- signatures ---------------------------------------------------------------

  const openSign = useCallback(() => {
    if (!session) return
    setSigBox(null)
    setSignOpen(true)
    void api.signatures(session.id).then(setSignReport).catch(() => undefined)
  }, [session])

  const signed = useCallback(
    async (r: SignatureReport & { session: SessionInfo }) => {
      setSignOpen(false)
      setSigBox(null)
      setSignReport(r)
      await reload(r.session)
      const last = r.signatures[r.signatures.length - 1]
      say('info', 'Signed', `Signed by ${last?.signer ?? 'you'}. Save now to keep the signature: any further change would show as made after signing.`)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [say],
  )

  // --- compare --------------------------------------------------------------------

  const runCompare = useCallback(
    async (blob: Blob, name: string, password = '', otherIsNewer = false) => {
      if (!session) return
      setBusy(true)
      try {
        const result = await api.compare(session.id, blob, name, { otherIsNewer, password })
        const other = await pdfjs.getDocument({ data: await blob.arrayBuffer(), password }).promise
        setComparePassword(null)
        setCompare((previous) => {
          void previous?.other.destroy()
          return { result, other, otherName: name, blob, password }
        })
      } catch (err) {
        const code = err instanceof ApiError ? (err.detail as { code?: string })?.code : undefined
        if (code === 'password_required' || code === 'password_incorrect') {
          setComparePassword({ blob, name, wrong: code === 'password_incorrect' })
        } else {
          say('fail', 'Could not compare', err instanceof Error ? err.message : String(err))
        }
      } finally {
        setBusy(false)
      }
    },
    [session, say],
  )

  const compareWithFile = useCallback(async () => {
    const file = await pickFile()
    if (file) await runCompare(file.blob, file.name)
  }, [runCompare])

  // --- measuring ------------------------------------------------------------------

  const measure = useCallback(
    async (pageIndex: number, which: MeasureTool, points: number[]) => {
      if (!session) return
      if (which === 'calibrate') {
        setTool('select')
        setScaleDialog({ pageIndex, calibration: points })
        return
      }
      const kind = which.slice(8) as 'distance' | 'perimeter' | 'area'
      try {
        const r = await api.measure(session.id, pageIndex, kind, points)
        await reload(r.session)
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err)
        if (/scale/i.test(message)) setScaleDialog({ pageIndex, calibration: null })
        say('warn', 'Not measured', message)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session, say],
  )

  const saveScale = useCallback(
    async (body: Parameters<typeof api.setScale>[1], label: string) => {
      if (!session) return
      const r = await run('Could not set the scale', () => api.setScale(session.id, body))
      if (!r) return
      setScaleDialog(null)
      setScales(r.pages.map((p) => p.scales))
      setSession(r.session)
      say('info', 'Scale', label)
    },
    [session, run, say],
  )

  // --- preparing a form -------------------------------------------------------------

  const afterFieldChange = useCallback(
    async (r: { session: SessionInfo; name?: string }) => {
      await reload(r.session)
      if (r.name) setSelectedField(r.name)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const addField = useCallback(
    async (pageIndex: number, kind: FieldBuildKind, rect: { x0: number; y0: number; x1: number; y1: number }) => {
      if (!session) return
      const selected = formFields.find((f) => f.name === selectedField)
      const r = await run('Could not add the field', () => api.addField(session.id, {
        pageIndex, kind, rect: [rect.x0, rect.y0, rect.x1, rect.y1],
        name: kind === 'radio' && selected?.kind === 'radio' ? selected.name : '',
        options: kind === 'combo' || kind === 'list' ? ['Choice 1', 'Choice 2', 'Choice 3'] : [],
      }))
      if (r) {
        await afterFieldChange(r)
        setTool('prepare')
      }
    },
    [session, run, formFields, selectedField, afterFieldChange],
  )

  const changeField = useCallback(
    async (changes: Record<string, unknown>, name = selectedField) => {
      if (!session || !name) return
      const r = await run('Could not change the field', () => api.updateField(session.id, name, changes))
      if (r) await afterFieldChange(r)
    },
    [session, run, selectedField, afterFieldChange],
  )

  const removeField = useCallback(async () => {
    if (!session || !selectedField) return
    const r = await run('Could not delete the field', () => api.deleteField(session.id, selectedField))
    if (r) {
      setSelectedField(null)
      await reload(r.session)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, run, selectedField])

  const detectFields = useCallback(async () => {
    if (!session) return
    const r = await run('Could not look for fields', () => api.detectFields(session.id, current))
    if (!r) return
    setSuggestions((s) => ({ ...s, [current]: r.suggestions }))
    say('info', 'Detect fields', r.suggestions.length
      ? `${r.suggestions.length} possible field(s) on page ${current + 1}. Tick the ones to keep, or Add all.`
      : `No blanks, boxes or lines for fields found on page ${current + 1}.`)
  }, [session, run, current, say])

  const acceptSuggestions = useCallback(
    async (pageIndex: number, which: number[]) => {
      if (!session) return
      const list = suggestions[pageIndex] ?? []
      const chosen = which.map((i) => list[i]).filter(Boolean)
      const r = await run('Could not add the fields', () => api.addFields(session.id, chosen.map((s) => ({
        pageIndex, kind: s.kind, rect: s.rect, name: s.name, tooltip: s.name,
        options: s.kind === 'combo' || s.kind === 'list' ? ['Choice 1'] : [],
      }))))
      if (!r) return
      setSuggestions((s) => ({ ...s, [pageIndex]: list.filter((_, i) => !which.includes(i)) }))
      await reload(r.session)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session, run, suggestions],
  )

  // Delete removes the selected field, while preparing (not while typing).
  useEffect(() => {
    if (tool !== 'prepare' || !selectedField) return
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement
      if (event.key !== 'Delete' || target.closest('input, textarea, select, [contenteditable]')) return
      event.preventDefault()
      void removeField()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [tool, selectedField, removeField])

  // --- accessibility --------------------------------------------------------------

  const bookmarksFromHeadings = useCallback(
    async (blocks: StructureBlock[]) => {
      if (!session) return
      const tree: Bookmark[] = []
      const stack: { level: number; node: Bookmark }[] = []
      for (const b of blocks) {
        const m = /^H([1-6])$/.exec(b.type)
        if (!m || b.pageIndex === null || !b.text) continue
        const level = Number(m[1])
        const node: Bookmark = { title: b.text.slice(0, 120), pageIndex: b.pageIndex, top: b.box ? b.box[3] : null, open: level === 1, children: [] }
        while (stack.length && stack[stack.length - 1].level >= level) stack.pop()
        if (stack.length) stack[stack.length - 1].node.children.push(node)
        else tree.push(node)
        stack.push({ level, node })
      }
      if (!tree.length) {
        say('warn', 'No headings', 'Add tags, or mark headings in Reading order, first.')
        return
      }
      if (await saveBookmarks(tree)) {
        setSidePanel('bookmarks')
        say('info', 'Bookmarks', `Made ${tree.length} top-level bookmark(s) from the headings.`)
      }
    },
    [session, saveBookmarks, say],
  )

  // --- forms ------------------------------------------------------------------

  const fillField = useCallback(
    async (name: string, value: string | string[] | boolean | null) => {
      if (!session) return
      const r = await run('Could not fill in the field', () => api.fillForm(session.id, { [name]: value }))
      if (!r) return
      await reload(r.session)
      if (r.problems.length) say('warn', 'Form', r.problems.join(' '))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session, run, say],
  )

  const clearForm = useCallback(async () => {
    if (!session || !window.confirm('Clear every field back to its default?')) return
    const r = await run('Could not clear the form', () => api.resetForm(session.id))
    if (r) await reload(r.session)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, run])

  const lockForm = useCallback(async () => {
    if (!session) return
    if (!window.confirm(
      'Lock the answers in?\n\nThe fields become part of the page and can no longer be ' +
      'changed by anyone. Signature fields stay signable. (Undo works until you close.)',
    )) return
    const r = await run('Could not lock the answers in', () => api.flattenForm(session.id))
    if (!r) return
    await reload(r.session)
    say('info', 'Answers locked in', `${r.flattened} field(s) are now part of the page.`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, run, say])

  // --- pages ----------------------------------------------------------------

  const reload = useCallback(
    async (info: SessionInfo) => {
      // A comparison shows the document as it was; after a change it would
      // go on showing stale differences over the changed page.
      setCompare((previous) => {
        if (previous) void previous.other.destroy()
        return null
      })
      await refresh(info)
    },
    [refresh],
  )

  const deletePages = useCallback(
    async (indices: number[]) => {
      if (!session) return
      const r = await run('Could not delete pages', () =>
        api.deletePages(session.id, indices),
      )
      if (r) {
        await reload(r.session)
        setSelectedPages(new Set())
        setCurrent((c) => Math.min(c, r.session.pageCount - 1))
      }
    },
    [session, run, reload],
  )

  const reorderPages = useCallback(
    async (order: number[]) => {
      if (!session) return
      const r = await run('Could not reorder pages', () =>
        api.reorderPages(session.id, order),
      )
      if (r) await reload(r.session)
    },
    [session, run, reload],
  )

  const rotatePages = useCallback(
    async (indices: number[], degrees: number) => {
      if (!session) return
      const r = await run('Could not rotate', () =>
        api.rotatePages(session.id, indices, degrees),
      )
      if (r) await reload(r.session)
    },
    [session, run, reload],
  )

  const mergeFiles = useCallback(
    async (files: File[], passwords: string[] = []) => {
      if (!session || !files.length) return
      setBusy(true)
      try {
        const r = await api.merge(session.id, files, undefined, passwords)
        setMergePassword(null)
        await reload(r.session)
        say('info', 'Combined', `Added ${r.added} page(s).`)
      } catch (err) {
        const detail = err instanceof ApiError ? (err.detail as { code?: string; index?: number }) : undefined
        const code = detail?.code
        if (code === 'password_required' || code === 'password_incorrect' || code === 'permissions_required') {
          // Ask for this file's password, then try the whole combine again.
          setMergePassword({
            files, passwords, index: detail?.index ?? 0,
            // Wrong only if this was already the permissions password being asked for.
            wrong: code === 'password_incorrect'
              || (code === 'permissions_required' && Boolean(mergePassword?.permissions)),
            permissions: code === 'permissions_required',
          })
          return
        }
        setMergePassword(null)
        say('fail', 'Could not combine', err instanceof Error ? err.message : String(err))
      } finally {
        setBusy(false)
      }
    },
    [session, reload, say, mergePassword],
  )

  // --- objects --------------------------------------------------------------

  const placeText = useCallback(
    async (pageIndex: number, x: number, y: number, suggested = '') => {
      if (!session) return
      const text = window.prompt(suggested ? 'Date to add:' : 'Text to add:', suggested)
      if (!text?.trim()) return
      // Upright as the page is shown, whatever its /Rotate.
      const rotation = pages[pageIndex]?.rotation ?? 0
      const r = await run('Could not add text', () =>
        api.addText(session.id, { pageIndex, x, y, text, fontSize: 12, rotation }),
      )
      if (r) {
        await reload(r.session)
        setSelectedObject(r.object.id)
        setTool('select')
      }
    },
    [session, run, reload, pages],
  )

  const placeImage = useCallback(
    async (file: Blob, pageIndex: number, x: number, y: number, kind: 'image' | 'signature') => {
      if (!session) return
      const r = await run('Could not add image', () =>
        api.addImage(session.id, file, { pageIndex, x, y, kind }),
      )
      if (r) {
        await reload(r.session)
        setSelectedObject(r.object.id)
        setTool('select')
      }
    },
    [session, run, reload],
  )

  const commitObject = useCallback(
    async (id: string, patch: Record<string, number | undefined>) => {
      if (!session) return
      // Only what changed: a move sends no size, and spreading "width:
      // undefined" over the object wiped its size, collapsing its frame to a
      // small box in the wrong place.
      const changes = Object.fromEntries(
        Object.entries(patch).filter(([, value]) => value !== undefined),
      ) as Record<string, number>
      // Optimistic: the layer already moved locally, so keep it there.
      setObjects((previous) =>
        previous.map((o) => (o.id === id ? { ...o, ...changes } : o)),
      )
      const r = await run('Could not move object', () =>
        api.updateObject(session.id, id, changes),
      )
      // Redraw the page: the object is drawn into it, so until this the
      // picture stayed where it was while only its outline moved.
      if (r) await reload(r.session)
      else await reload(session)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session, run],
  )

  const deleteObject = useCallback(
    async (id: string) => {
      if (!session) return
      const r = await run('Could not delete object', () =>
        api.deleteObject(session.id, id),
      )
      if (r) {
        setSelectedObject(null)
        await reload(r.session)
      }
    },
    [session, run, reload],
  )

  const editObjectText = useCallback(
    async (object: PlacedObject) => {
      if (!session) return
      const text = window.prompt('Edit text:', object.text)
      if (text === null || text === object.text) return
      const r = await run('Could not edit object', () =>
        api.updateObject(session.id, object.id, { text }),
      )
      if (r) await reload(r.session)
    },
    [session, run, reload],
  )

  const handleSignature = useCallback(
    async (result: SignatureResult) => {
      setSignatureOpen(false)
      if (!session) return
      const spot = pendingPlace ?? { pageIndex: current, x: 90, y: 120 }
      setPendingPlace(null)

      if (result.kind === 'typed') {
        const r = await run('Could not add signature', () =>
          api.addTypedSignature(session.id, {
            pageIndex: spot.pageIndex,
            x: spot.x,
            y: spot.y,
            text: result.text,
            fontSize: 26,
          }),
        )
        if (r) {
          await reload(r.session)
          setSelectedObject(r.object.id)
        }
      } else {
        await placeImage(result.blob, spot.pageIndex, spot.x, spot.y, 'signature')
      }
      setTool('select')
    },
    [session, current, pendingPlace, run, reload, placeImage],
  )

  // --- text -----------------------------------------------------------------

  const showFidelity = useCallback((level: FidelityLevel, text: string) => {
    setFidelity(level)
    setFidelityText(text)
  }, [])

  const handleProbe = useCallback(
    async (pageIndex: number, runItem: Run, text: string) => {
      if (!session) return
      try {
        const probe = await api.probe(session.id, pageIndex, runItem.index, text)
        lastProbe.current = { key: `${pageIndex}:${runItem.index}:${text}`, probe }
        const shown = describeFidelity(probe, false)
        if (probe.localFont && localFontsSupported() && shown.level !== 'exact') {
          shown.text += ' \u2014 Odie will ask to use the copy installed on this computer'
        }
        showFidelity(shown.level, shown.text)
      } catch {
        /* advisory only; never block typing */
      }
    },
    [session, showFidelity],
  )

  /**
   * Before an edit that needs letters the server cannot supply exactly, try
   * the font installed on this computer -- the one the document was made
   * with. The first time, the browser asks the user's permission.
   */
  const offerLocalFont = useCallback(
    async (pageIndex: number, runItem: Run, text: string) => {
      if (!session || !localFontsSupported()) return
      const key = `${pageIndex}:${runItem.index}:${text}`
      let probe = lastProbe.current?.key === key ? lastProbe.current.probe : null
      if (!probe) {
        try {
          probe = await api.probe(session.id, pageIndex, runItem.index, text)
        } catch {
          return
        }
      }
      if (!probe.localFont || !probe.fontFamily) return
      const font = await findLocalFont(probe.localFont)
      if (!font) return
      try {
        await api.uploadFont(session.id, font, probe.fontFamily)
      } catch (err) {
        // A font the licence forbids embedding is refused here; say why,
        // then let the edit continue with what the server has.
        say('warn', 'Could not use this computer\u2019s font',
          err instanceof Error ? err.message : String(err))
      }
    },
    [session, say],
  )

  const handleCommit = useCallback(
    async (pageIndex: number, runItem: Run, text: string) => {
      if (!session) return
      setBusy(true)
      try {
        // Before any other await: the browser's font permission prompt needs
        // the user's keystroke or click to still count as recent.
        await offerLocalFont(pageIndex, runItem, text)
        const { report, session: updated } = await api.edit(
          session.id, pageIndex, runItem.index, text,
        )
        await refresh(updated)
        const shown = describeFidelity(
          { fidelity: report.fidelity, additions: report.additions ?? [], unresolved: [] },
          true,
        )
        showFidelity(shown.level, shown.text)
        if (report.fidelity === 'substituted') {
          const close = report.additions.filter((a) => !a.exact)
          // An overlap is the more urgent news; neither may hide the other.
          say(
            'warn',
            report.overlaps?.length ? 'Close match used, and the edit overlaps nearby text' : 'Close match used',
            `${letters(close.map((a) => a.char))} came from ${sourcesOf(close)} ` +
              `(${close[0]?.note}). Everything else keeps the original font.` +
              (report.overlaps?.length ? ' ' + report.warnings.join(' ') : ''),
          )
        } else if (report.overlaps?.length) {
          say('warn', 'Your edit overlaps nearby text', report.warnings.join(' '))
        } else if (report.warnings.length) {
          say('warn', 'Edit applied with a caveat', report.warnings.join(' '))
        }
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          const detail = err.detail as EditReport
          showFidelity('fail', describeFidelity(
            { fidelity: 'failed', additions: [], unresolved: detail.missingGlyphs ?? [] },
            true,
          ).text)
          say(
            'fail',
            'No font can draw this',
            (detail.warnings ?? []).join(' ') ||
              `Nothing available can draw ${letters(detail.missingGlyphs ?? [])}.`,
          )
        } else {
          say('fail', 'Edit failed', err instanceof Error ? err.message : String(err))
        }
      } finally {
        setBusy(false)
      }
    },
    [session, refresh, say, offerLocalFont, showFidelity],
  )

  const deleteParagraph = useCallback(
    async (pageIndex: number, runIndices: number[]) => {
      if (!session) return
      const r = await run('Could not delete paragraph', () =>
        api.deleteRuns(session.id, pageIndex, runIndices),
      )
      if (r) {
        await reload(r.session)
        setTool('select')
      }
    },
    [session, run, reload],
  )

  const moveRun = useCallback(
    async (pageIndex: number, runIndex: number, dx: number, dy: number) => {
      if (!session) return
      const r = await run('Could not move text', () =>
        api.moveRun(session.id, pageIndex, runIndex, dx, dy),
      )
      if (r) await reload(r.session)
    },
    [session, run, reload],
  )

  // --- comments -------------------------------------------------------------

  const addComment = useCallback(
    async (
      pageIndex: number,
      kind: 'note' | 'highlight',
      rect: { x0: number; y0: number; x1: number; y1: number },
      quads?: { x0: number; y0: number; x1: number; y1: number }[],
    ) => {
      if (!session) return
      const text = window.prompt(
        kind === 'note' ? 'Comment:' : 'Comment on the highlighted text:',
      )
      if (text === null) return

      const r = await run('Could not add comment', () =>
        api.addComment(session.id, {
          pageIndex,
          kind,
          rect,
          contents: text,
          quads: kind === 'highlight' ? (quads ?? [rect]) : undefined,
        }),
      )
      if (!r) return

      await reload(r.session)
      setCommentsOpen(true)
      setActiveComment(r.comment.id)
      setTool('select')
    },
    [session, run, reload],
  )

  /** A markup drawn on the page: underline, shapes, lines, pen, highlight. */
  const addMarkup = useCallback(
    async (pageIndex: number, kind: CommentKind, geometry: MarkupGeometry) => {
      if (!session) return
      if (kind === 'highlight') {
        // Highlights carry a comment, as before.
        await addComment(pageIndex, 'highlight', geometry.rect, geometry.quads)
        return
      }
      const r = await run('Could not add markup', () =>
        api.addComment(session.id, {
          pageIndex, kind, rect: geometry.rect, quads: geometry.quads,
          points: geometry.points, ink: geometry.ink, arrow: geometry.arrow,
        }),
      )
      if (r) await reload(r.session)
      // The tool stays armed, as Acrobat's do: drawing is usually several marks.
    },
    [session, run, reload, addComment],
  )

  const placeStamp = useCallback(
    async (stamp: StampKind, detail: string) => {
      if (!session || !stampAt) return
      const { pageIndex, x, y } = stampAt
      setStampAt(null)
      const r = await run('Could not place the stamp', () =>
        api.addComment(session.id, {
          pageIndex, kind: 'stamp', rect: { x0: x, y0: y, x1: x, y1: y },
          stamp, stampDetail: detail,
        }),
      )
      if (r) await reload(r.session)
    },
    [session, stampAt, run, reload],
  )

  const replyToComment = useCallback(
    async (id: string, text: string) => {
      if (!session) return
      const parent = threads.find((t) => t.comment.id === id)?.comment
      if (!parent) return

      const r = await run('Could not reply', () =>
        api.addComment(session.id, {
          pageIndex: parent.pageIndex,
          kind: 'note',
          rect: {
            x0: parent.rect[0], y0: parent.rect[1],
            x1: parent.rect[2], y1: parent.rect[3],
          },
          contents: text,
          inReplyTo: id,
        }),
      )
      if (r) await reload(r.session)
    },
    [session, threads, run, reload],
  )

  const editComment = useCallback(
    async (id: string, text: string) => {
      if (!session) return
      const r = await run('Could not edit comment', () =>
        api.updateComment(session.id, id, { contents: text }),
      )
      if (r) await reload(r.session)
    },
    [session, run, reload],
  )

  const deleteComment = useCallback(
    async (id: string) => {
      if (!session) return
      const r = await run('Could not delete comment', () =>
        api.deleteComment(session.id, id),
      )
      if (!r) return
      await reload(r.session)
      setActiveComment(null)
      if (r.removed > 1) {
        say('info', 'Comment deleted', `Its ${r.removed - 1} repl${r.removed === 2 ? 'y' : 'ies'} went too.`)
      }
    },
    [session, run, reload, say],
  )

  // --- reorganising ---------------------------------------------------------

  const duplicatePages = useCallback(
    async (indices: number[]) => {
      if (!session) return
      const r = await run('Could not duplicate', () =>
        api.duplicatePages(session.id, indices),
      )
      if (r) {
        await reload(r.session)
        say('info', 'Duplicated', `${r.created.length} page(s) copied.`)
      }
    },
    [session, run, reload, say],
  )

  const insertBlank = useCallback(
    async (at: number) => {
      if (!session) return
      const r = await run('Could not insert page', () =>
        api.insertBlank(session.id, at),
      )
      if (r) {
        await reload(r.session)
        say('info', 'Blank page inserted', `At position ${at + 1}.`)
      }
    },
    [session, run, reload, say],
  )

  // --- extraction -----------------------------------------------------------

  const saveBlob = (blob: Blob, name: string) => {
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = name
    anchor.click()
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000)
  }

  const stem = useCallback(
    () => (session?.filename ?? 'document').replace(/\.pdf$/i, ''),
    [session],
  )

  const extractImages = useCallback(async () => {
    if (!session) return
    const listed = await run('Could not read images', () =>
      api.listImages(session.id),
    )
    if (!listed) return
    if (!listed.count) {
      say('info', 'No images', 'This document has no extractable images.')
      return
    }

    const blob = await run('Could not extract images', () =>
      api.downloadImages(session.id),
    )
    if (!blob) return
    saveBlob(blob, `${stem()}_images.zip`)

    const untouched = listed.images.filter((i) => i.encoding === 'original').length
    say(
      'info',
      'Images extracted',
      `${listed.count} image(s)` +
        (untouched ? `, ${untouched} handed back byte-identical` : '') +
        '.',
    )
  }, [session, run, say, stem])

  const extractText = useCallback(
    async (fmt: 'txt' | 'json') => {
      if (!session) return
      const blob = await run('Could not extract text', () =>
        api.downloadText(session.id, fmt),
      )
      if (!blob) return
      saveBlob(blob, `${stem()}.${fmt}`)
      say('info', 'Text extracted', `Saved as ${fmt.toUpperCase()}.`)
    },
    [session, run, say, stem],
  )

  // --- object stacking ------------------------------------------------------

  const arrangeObject = useCallback(
    async (action: ArrangeAction) => {
      if (!session || !selectedObject) return
      const r = await run('Could not arrange', () =>
        api.arrangeObject(session.id, selectedObject, action),
      )
      if (r) await reload(r.session)
    },
    [session, selectedObject, run, reload],
  )

  // --- crop -----------------------------------------------------------------

  const cropTo = useCallback(
    async (
      pageIndex: number,
      rect: { x0: number; y0: number; x1: number; y1: number },
      destructive: boolean,
    ) => {
      if (!session) return

      if (destructive) {
        const ok = window.confirm(
          'Destructive crop permanently removes everything outside the box on ' +
            `page ${pageIndex + 1}. It cannot be recovered after saving.\n\n` +
            'Continue?',
        )
        if (!ok) return
      }

      const r = await run('Crop failed', () =>
        api.crop(session.id, [pageIndex], rect, destructive),
      )
      if (!r) return

      await reload(r.session)
      setTool('select')
      say(
        'info',
        destructive ? 'Cropped (content removed)' : 'Cropped',
        destructive
          ? 'Everything outside the box was destroyed. Save will flatten the file.'
          : 'Page boxes were set. The trimmed content is still in the file — ' +
            'use a destructive crop if it must not be recoverable.',
      )
    },
    [session, run, reload, say],
  )

  const cropToContent = useCallback(async () => {
    if (!session) return
    const bounds = await run('Could not measure content', () =>
      api.contentBounds(session.id, current),
    )
    if (!bounds) return
    if (!bounds.content) {
      say('warn', 'Nothing to trim', 'No measurable content was found on this page.')
      return
    }
    const [x0, y0, x1, y1] = bounds.content
    await cropTo(current, { x0, y0, x1, y1 }, cropDestructive)
  }, [session, current, cropDestructive, run, say, cropTo])

  // Picking a tool that sets new text is the click that may ask for the font.
  useEffect(() => {
    if (session && (NEW_TEXT_TOOLS.has(tool) || marksOpen)) {
      void handOverDefaultFont(session.id, true)
    }
  }, [session, tool, marksOpen, handOverDefaultFont])

  // --- OCR ------------------------------------------------------------------

  const runOcr = useCallback(
    async (pageIndex: number) => {
      if (!session) return
      await handOverDefaultFont(session.id, true)
      say('info', 'Running OCR', `Recognising page ${pageIndex + 1}\u2026`)
      const r = await run('OCR failed', () => api.ocr(session.id, pageIndex))
      if (!r) return

      await reload(r.session)
      setOcrPages((previous) => new Set(previous).add(pageIndex))

      const { result } = r
      const detail =
        `${r.linesApplied} line(s) placed by ${result.engine} ` +
        `(${(result.meanConfidence * 100).toFixed(0)}% mean confidence, ` +
        `${result.elapsed}s)` +
        (result.textCorrections
          ? `, ${result.textCorrections} corrected by Swifty`
          : '')

      say(
        result.flagged.length ? 'warn' : 'info',
        'OCR complete',
        result.flagged.length
          ? `${detail}. Needs review: ${result.flagged.slice(0, 3).join(' \u00b7 ')}`
          : detail,
      )
    },
    [session, run, reload, say],
  )

  // --- redaction ------------------------------------------------------------

  const scanForSensitive = useCallback(async () => {
    if (!session) return
    const r = await run('Scan failed', () => api.redactScan(session.id))
    if (!r) return
    setFoundRegions(r.regions)
    setRedactPanelOpen(true)
    if (!r.regions.length) {
      say('info', 'Nothing found', 'No sensitive values matched on any page.')
    }
  }, [session, run, say])

  const applyRedactions = useCallback(
    async (regions: RedactionRegion[]) => {
      if (!session || !regions.length) return

      // One call per page, since each redaction is applied page by page.
      const byPage = new Map<number, RedactionRegion[]>()
      for (const region of regions) {
        const list = byPage.get(region.pageIndex) ?? []
        list.push(region)
        byPage.set(region.pageIndex, list)
      }

      setBusy(true)
      try {
        let last: SessionInfo | null = null
        let destroyed = 0
        for (const [pageIndex, list] of byPage) {
          const r = await api.redact(session.id, pageIndex, { auto: list })
          last = r.session
          destroyed += r.report.regions
        }
        if (last) await reload(last)

        const verdict = await api.redactVerify(session.id)
        setRedactPanelOpen(false)
        setFoundRegions([])
        say(
          verdict.verified ? 'info' : 'fail',
          verdict.verified ? 'Redacted and verified' : 'Redaction NOT verified',
          verdict.verified
            ? `${destroyed} region(s) destroyed and verified unrecoverable. ` +
              'Save will flatten the file.'
            : `Still recoverable: ${verdict.leaked.join(', ')}. Do not release this file.`,
        )
      } catch (err) {
        say('fail', 'Redaction failed',
            err instanceof Error ? err.message : String(err))
      } finally {
        setBusy(false)
      }
    },
    [session, reload, say],
  )

  /** Make a PDF from pictures and Office files, then open it. */
  const createFromFiles = useCallback(async (files: File[]) => {
    setBusy(true)
    try {
      const created = await createPdf(files, (message) => say('info', 'Creating PDF', message))
      await openFile({ name: created.name, blob: created.blob, source: 'local' })
      say('info', 'PDF created', `${created.name} is open. Save it to keep it.`)
    } catch (err) {
      say('fail', 'Could not create the PDF', err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [say])

  const flattenDocument = useCallback(async () => {
    if (!session) return
    if (!window.confirm(
      'Flatten the document?\n\nText boxes, images, typed signatures, form answers and comments ' +
      'become part of the pages: they stay where they are, but can no longer be moved, edited ' +
      'or deleted. (Undo works until you close.)',
    )) return
    const r = await run('Could not flatten', () => api.flatten(session.id))
    if (!r) return
    setSelectedObject(null)
    await reload(r.session)
    const parts = [
      r.objects && `${r.objects} page(s) of placed objects`,
      r.fields && `${r.fields} form field(s)`,
      r.comments && `${r.comments} comment(s)`,
    ].filter(Boolean)
    say('info', 'Flattened', parts.length ? `Now part of the pages: ${parts.join(', ')}.` : 'There was nothing to flatten.')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, run, say])

  /** Whiteout a box, then offer to type over it (placed like a text box, in
   * the box, at a size that fits it). */
  const whiteoutRect = useCallback(
    async (pageIndex: number, rect: { x0: number; y0: number; x1: number; y1: number }) => {
      if (!session) return
      const r = await run('Whiteout failed', () => api.whiteout(session.id, pageIndex, [rect]))
      if (!r) return
      await reload(r.session)
      const text = window.prompt('Whited out. Text to put in its place (leave empty for none):', '')
      if (!text?.trim()) return
      const height = rect.y1 - rect.y0
      const size = Math.max(6, Math.min(24, height * 0.72))
      const rotation = pages[pageIndex]?.rotation ?? 0
      const placed = await run('Could not add the text', () => api.addText(session.id, {
        pageIndex, x: rect.x0 + 2, y: rect.y0 + (height - size) / 2 + size * 0.2,
        text, fontSize: Math.round(size * 2) / 2, rotation,
      }))
      if (placed) {
        await reload(placed.session)
        setSelectedObject(placed.object.id)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session, run, pages],
  )

  const redactRect = useCallback(
    async (
      pageIndex: number,
      rect: { x0: number; y0: number; x1: number; y1: number },
    ) => {
      if (!session) return
      const r = await run('Redaction failed', () =>
        api.redact(session.id, pageIndex, { rects: [rect] }),
      )
      if (!r) return

      await reload(r.session)
      const verdict = await api.redactVerify(session.id)
      say(
        verdict.verified ? 'info' : 'fail',
        verdict.verified ? 'Area redacted' : 'Redaction NOT verified',
        verdict.verified
          ? 'Content destroyed' +
            (r.report.imagesModified ? ' (including image pixels)' : '') +
            '. Save will flatten the file.'
          : `Still recoverable: ${verdict.leaked.join(', ')}.`,
      )
    },
    [session, run, reload, say],
  )

  // --- history --------------------------------------------------------------

  const history = useCallback(
    async (direction: 'undo' | 'redo') => {
      if (!session) return
      const r = await run('Nothing to ' + direction, () =>
        direction === 'undo' ? api.undo(session.id) : api.redo(session.id),
      )
      if (r) await reload(r)
    },
    [session, run, reload],
  )

  // --- canvas click routing -------------------------------------------------

  const handleCanvasClick = useCallback(
    (pageIndex: number, x: number, y: number) => {
      if (tool === 'note') {
        // A sticky note is a fixed-size icon anchored where the user clicked.
        void addComment(pageIndex, 'note', {
          x0: x, y0: y - 24, x1: x + 24, y1: y,
        })
      } else if (tool === 'text') void placeText(pageIndex, x, y)
      else if (tool === 'date') {
        // Today, spelled out so it reads the same everywhere; editable first.
        const today = new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })
        void placeText(pageIndex, x, y, today)
      }
      else if (tool === 'image') {
        setPendingPlace({ pageIndex, x, y })
        imageInput.current?.click()
      } else if (tool === 'signature') {
        setPendingPlace({ pageIndex, x, y })
        setSignatureOpen(true)
      } else if (tool === 'stamp') {
        setStampAt({ pageIndex, x, y })
      }
    },
    [tool, placeText, addComment],
  )

  // Ctrl+F and Ctrl+H open Odie's find, not the browser's: the browser's
  // would search the interface, not the document.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!session || !(event.ctrlKey || event.metaKey)) return
      const key = event.key.toLowerCase()
      if (key === 'f' || key === 'h') {
        event.preventDefault()
        setFind({ replace: key === 'h' })
        return
      }
      // Ctrl+Z / Ctrl+Y (and Ctrl+Shift+Z) undo the document -- except while
      // typing in a field or a text run, where they undo the typing.
      const target = event.target as HTMLElement | null
      const typing = target?.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName ?? '')
      if (typing || event.altKey) return
      if (key === 'z' && !event.shiftKey) {
        event.preventDefault()
        void history('undo')
      } else if (key === 'y' || (key === 'z' && event.shiftKey)) {
        event.preventDefault()
        void history('redo')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [session, history])

  // Bring the current match into view as the user steps through them.
  useEffect(() => {
    if (!findMatches.length) return
    window.setTimeout(() => {
      document.getElementById('find-current')?.scrollIntoView({ block: 'center', inline: 'nearest' })
    }, 0)
  }, [findMatches, findCurrent])

  const tabs = useMemo<Tab[]>(
    () =>
      buildTabs({
        session,
        tool,
        setTool,
        onOpen: handleOpen,
        onSave: () => void handleSave(),
        onClose: () => guardUnsaved('close', closeDocument),
        onCertificateSign: openSign,
        onShowSignatures: () => setSignaturesOpen(true),
        hasSignatures: Boolean(signReport?.signatures.length),
        onCompareFile: () => void compareWithFile(),
        onCompareOneDrive: () => setOneDriveMode('compare'),
        askOpen,
        onToggleAsk: () => { setAccessOpen(false); setAskOpen((open) => !open) },
        onScale: () => setScaleDialog({ pageIndex: current, calibration: null }),
        onDetectFields: () => { setTool('prepare'); void detectFields() },
        accessOpen,
        onToggleAccess: () => { setAskOpen(false); setAccessOpen((open) => !open) },
        onSaveAs: () => void handleSaveAs(),
        oneDrive: graphAvailable(),
        onOneDriveOpen: () => setOneDriveMode('open'),
        onOneDriveSave: () => setOneDriveMode('save'),
        onCompress: () => setCompressOpen(true),
        onExportJpg: () => setExportOpen(true),
        onFlatten: () => void flattenDocument(),
        hasForm: formFields.length > 0,
        highlightFields,
        onToggleHighlight: () => setHighlightFields((on) => !on),
        onClearForm: () => void clearForm(),
        onLockForm: () => void lockForm(),
        bookmarksOpen: sidePanel === 'bookmarks',
        onToggleBookmarks: () => setSidePanel((p) => (p === 'bookmarks' ? 'pages' : 'bookmarks')),
        onCombine: () => mergeInput.current?.click(),
        onCreatePdf: () => createInput.current?.click(),
        onUndo: () => void history('undo'),
        onRedo: () => void history('redo'),
        onDeletePages: () => {
          const doomed = selectedPages.size ? [...selectedPages].sort((a, b) => a - b) : [current]
          if (window.confirm(doomed.length === 1
            ? `Delete page ${doomed[0] + 1}?`
            : `Delete ${doomed.length} pages (${doomed.map((i) => i + 1).join(', ')})?`)) {
            void deletePages(doomed)
          }
        },
        onRotate: () => void rotatePages(selectedPages.size ? [...selectedPages] : [current], 90),
        onOcr: () => void runOcr(current),
        onScanSensitive: () => void scanForSensitive(),
        onDuplicate: () =>
          void duplicatePages(selectedPages.size ? [...selectedPages] : [current]),
        onInsertBlank: () => void insertBlank(current + 1),
        onExtractImages: () => void extractImages(),
        onExtractText: () => void extractText('txt'),
        onExtractJson: () => void extractText('json'),
        onToggleComments: () => setCommentsOpen((v) => !v),
        commentsOpen,
        hasSelectedObject: Boolean(selectedObject),
        onArrange: (action: ArrangeAction) => void arrangeObject(action),
        onSplit: () => setSplitOpen(true),
        onCropContent: () => void cropToContent(),
        cropDestructive,
        toggleCropMode: () => setCropDestructive((v) => !v),
        onRotateCcw: () =>
          void rotatePages(selectedPages.size ? [...selectedPages] : [current], -90),
        onRotate180: () =>
          void rotatePages(selectedPages.size ? [...selectedPages] : [current], 180),
        onZoomIn: () => setScale((s) => Math.min(s * 1.2, 5)),
        onZoomOut: () => setScale((s) => Math.max(s / 1.2, 0.25)),
        onFit: () => setScale(fitScale(pageInfo[current])),
        onFind: () => setFind({ replace: false }),
        onReplace: () => setFind({ replace: true }),
        onMarks: () => setMarksOpen(true),
        onProtect: () => setProtectOpen(true),
      }),
    [
      session, tool, handleOpen, handleSave, handleSaveAs, history, deletePages, rotatePages,
      runOcr, selectedPages, current, scanForSensitive, cropToContent,
      cropDestructive, rotatePages, duplicatePages, insertBlank,
      extractImages, extractText, commentsOpen, selectedObject, arrangeObject,
      guardUnsaved, closeDocument, signReport, askOpen, accessOpen, formFields,
      highlightFields, sidePanel, pageInfo,
    ],
  )

  return (
    <div className="app">
      <header className="titlebar">
        <span className="titlebar__brand">ODIE</span>
        <span className="titlebar__doc">
          {session ? session.filename : 'No document open'}
        </span>
        <span className="titlebar__user">City of Swift Current</span>
      </header>

      <Ribbon tabs={tabs} />


      <div className="workspace">
        {find && session && (
          <FindBar
            sessionId={session.id}
            showReplace={find.replace}
            matches={findMatches}
            current={findCurrent}
            onResults={(matches, index) => {
              setFindMatches(matches)
              setFindCurrent(index)
            }}
            onReplaced={(info, summary) => {
              void reload(info)
              say(summary.level, summary.title, summary.body)
            }}
            onClose={() => {
              setFind(null)
              setFindMatches([])
            }}
          />
        )}
        {doc && pageInfo.length > 0 && sidePanel === 'bookmarks' && (
          <BookmarksPanel
            header={<SideTabs panel={sidePanel} onPanel={setSidePanel} bookmarks={bookmarks.length} />}
            bookmarks={bookmarks}
            pageCount={pageInfo.length}
            current={current}
            readOnly={Boolean(session?.locked)}
            onGoTo={goTo}
            onOpenUri={openExternal}
            onChange={saveBookmarks}
          />
        )}
        {doc && pageInfo.length > 0 && sidePanel === 'pages' && (
          <Thumbnails
            title={<SideTabs panel={sidePanel} onPanel={setSidePanel} bookmarks={bookmarks.length} />}
            doc={doc}
            pages={pageInfo}
            current={current}
            selected={selectedPages}
            ocrPages={ocrPages}
            onGoTo={(index) => {
              setCurrent(index)
              document
                .getElementById(`page-${index}`)
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }}
            onToggleSelect={(index, additive) =>
              setSelectedPages((previous) => {
                const next = additive ? new Set(previous) : new Set<number>()
                if (additive && previous.has(index)) next.delete(index)
                else next.add(index)
                return next
              })
            }
            onReorder={(order) => void reorderPages(order)}
            onDelete={(indices) => void deletePages(indices)}
            onRotate={(indices, degrees) => void rotatePages(indices, degrees)}
            onOcr={(index) => void runOcr(index)}
          />
        )}

        {(tool === 'prepare' || tool.startsWith('field-')) && session && (
          <FieldPanel
            field={formFields.find((f) => f.name === selectedField) ?? null}
            suggestions={(suggestions[current] ?? []).length}
            onChange={(changes) => void changeField(changes)}
            onDelete={() => void removeField()}
            onDetect={() => void detectFields()}
            onAcceptAll={() => void acceptSuggestions(current, (suggestions[current] ?? []).map((_, i) => i))}
            onDismissAll={() => setSuggestions((s) => ({ ...s, [current]: [] }))}
            onClose={() => { setTool('select'); setSelectedField(null) }}
          />
        )}

        {accessOpen && session && doc && !(tool === 'prepare' || tool.startsWith('field-')) && (
          <AccessibilityPanel
            sessionId={session.id}
            doc={doc}
            fields={formFields}
            selected={selectedBlock}
            onSelect={(block) => {
              setSelectedBlock(block?.id ?? null)
              if (block?.pageIndex !== null && block?.pageIndex !== undefined) goTo(block.pageIndex, block.box ? block.box[3] + 12 : null)
            }}
            onBlocks={setOrderBlocks}
            onChanged={(info) => reload(info)}
            onOcr={(pages) => void (async () => { for (const p of pages) await runOcr(p) })()}
            onBookmarks={(blocks) => void bookmarksFromHeadings(blocks)}
            onGoTo={(index) => goTo(index)}
            onClose={() => { setAccessOpen(false); setOrderBlocks([]); setSelectedBlock(null) }}
          />
        )}

        {askOpen && session && !accessOpen && !(tool === 'prepare' || tool.startsWith('field-')) && (
          <AskPanel
            // One panel per document: its conversation is its own.
            key={session.id}
            sessionId={session.id}
            filename={session.filename}
            onGoTo={(index) => goTo(Math.max(0, Math.min(index, session.pageCount - 1)))}
            onClose={() => setAskOpen(false)}
          />
        )}

        <CommentsPanel
          open={commentsOpen && Boolean(session) && !askOpen && !accessOpen && !(tool === 'prepare' || tool.startsWith('field-'))}
          threads={threads}
          activeId={activeComment}
          onClose={() => setCommentsOpen(false)}
          onSelect={(id, pageIndex) => {
            setActiveComment(id)
            setCurrent(pageIndex)
            document
              .getElementById(`page-${pageIndex}`)
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }}
          onReply={(id, text) => void replyToComment(id, text)}
          onEdit={(id, text) => void editComment(id, text)}
          onDelete={(id) => void deleteComment(id)}
        />

        {compare && session && doc && (
          <CompareView
            result={compare.result}
            older={compare.result.oldIs === 'other' ? compare.other : doc}
            newer={compare.result.oldIs === 'other' ? doc : compare.other}
            olderName={compare.result.oldIs === 'other' ? compare.otherName : session.filename}
            newerName={compare.result.oldIs === 'other' ? session.filename : compare.otherName}
            onSwap={() => void runCompare(compare.blob, compare.otherName, compare.password,
                                          compare.result.oldIs === 'other')}
            onClose={() => setCompare((previous) => {
              void previous?.other.destroy()
              return null
            })}
          />
        )}

        <main className="canvas" data-tool={tool}>
          {!session && (
            <div className="empty">
              <div className="empty__title">Open a PDF to begin</div>
              <p>
                Odie edits text in place, reusing the document&rsquo;s own embedded
                fonts. Nothing you don&rsquo;t touch is re-rendered.
              </p>
              {!capabilities.fileSystemAccess && (
                <p>
                  This browser cannot write files back in place. Use Chrome, Edge,
                  or Brave for full Save support.
                </p>
              )}
              {capabilities.fileHandling && !capabilities.installed && (
                <p>
                  Install Odie (address bar &rsaquo; Install) to open PDFs by
                  double-clicking them in File Explorer.
                </p>
              )}
            </div>
          )}

          {doc &&
            pages.map((page) => (
              <PageView
                key={page.pageIndex}
                doc={doc}
                page={page}
                scale={scale}
                tool={tool}
                objects={objects.filter((o) => o.pageIndex === page.pageIndex)}
                sessionId={session?.id ?? null}
                activeRun={activeRun?.pageIndex === page.pageIndex ? activeRun.key : null}
                setActiveRun={(key, ending) => setActiveRun((previous) => {
                  if (key !== null) return { pageIndex: page.pageIndex, key }
                  // Leaving one box must not close another the user has
                  // already moved to (its blur comes after the new click).
                  if (ending !== undefined && previous
                      && (previous.pageIndex !== page.pageIndex || previous.key !== ending)) return previous
                  return null
                })}
                onVisibility={onVisibility}
                selectedObject={selectedObject}
                setSelectedObject={setSelectedObject}
                onCommit={(r, text) => void handleCommit(page.pageIndex, r, text)}
                onProbe={(r, text) => void handleProbe(page.pageIndex, r, text)}
                onCanvasClick={(x, y) => handleCanvasClick(page.pageIndex, x, y)}
                onObjectCommit={(id, patch) => void commitObject(id, patch)}
                onObjectDelete={(id) => void deleteObject(id)}
                onObjectEditText={(o) => void editObjectText(o)}
                onDeleteParagraph={(indices) => void deleteParagraph(page.pageIndex, indices)}
                onMoveRun={(runIndex, dx, dy) => void moveRun(page.pageIndex, runIndex, dx, dy)}
                onRedactRect={(rect) => void redactRect(page.pageIndex, rect)}
                onWhiteoutArea={(rect) => void whiteoutRect(page.pageIndex, rect)}
                onCropRect={(rect) => void cropTo(page.pageIndex, rect, cropDestructive)}
                onMarkup={(kind, geometry) => void addMarkup(page.pageIndex, kind, geometry)}
                findMatches={findMatches.filter((m) => m.pageIndex === page.pageIndex)}
                currentMatch={findMatches[findCurrent] ?? null}
                comments={threads.flatMap((t) => [t.comment, ...t.replies])}
                activeComment={activeComment}
                onSelectComment={(id) => {
                  setActiveComment(id)
                  setCommentsOpen(true)
                }}
                formFields={formFields.filter((f) => f.widgets.some((w) => w.pageIndex === page.pageIndex))}
                highlightFields={highlightFields}
                onFieldChange={(name, value) => void fillField(name, value)}
                links={links.filter((l) => l.pageIndex === page.pageIndex)}
                onFollowLink={followLink}
                onDeleteLink={(id) => void deleteLink(id)}
                onLinkArea={(rect) => setLinkArea({
                  pageIndex: page.pageIndex, rect: [rect.x0, rect.y0, rect.x1, rect.y1] })}
                onEditParagraph={(paragraph) => setEditParagraph({ pageIndex: page.pageIndex, paragraph })}
                scales={scales[page.pageIndex] ?? []}
                onMeasure={(which, points) => void measure(page.pageIndex, which, points)}
                selectedField={selectedField}
                onSelectField={setSelectedField}
                onFieldGeometry={(name, widget, rect) => void changeField({ rect, widget }, name)}
                onFieldArea={(kind, rect) => void addField(page.pageIndex, kind, rect)}
                suggestions={suggestions[page.pageIndex] ?? []}
                onAcceptSuggestion={(i) => void acceptSuggestions(page.pageIndex, [i])}
                onDismissSuggestion={(i) => setSuggestions((s) => ({
                  ...s, [page.pageIndex]: (s[page.pageIndex] ?? []).filter((_, j) => j !== i) }))}
                orderBlocks={orderBlocks.map((block, i) => ({ block, number: i + 1 }))
                  .filter(({ block }) => block.pageIndex === page.pageIndex)}
                selectedBlock={selectedBlock}
                onSelectBlock={(block) => setSelectedBlock(block.id)}
                onSignatureArea={(rect) => {
                  setSigBox({ pageIndex: page.pageIndex, rect: [rect.x0, rect.y0, rect.x1, rect.y1] })
                  setTool('select')
                }}
              />
            ))}
        </main>
      </div>

      <footer className="statusbar">
        <span>{session ? `Page ${current + 1} of ${session.pageCount}` : 'Ready'}</span>
        <span className="fidelity" data-level={fidelity}>
          <span className="fidelity__dot" />
          {fidelityText}
        </span>
        <span className="statusbar__spacer" />
        {dirty && (
          <span className="statusbar__unsaved" title="There are changes that have not been saved">
            {'\u25CF'} Unsaved changes
          </span>
        )}
        {signReport && signReport.signatures.length > 0 && (() => {
          const bad = signReport.signatures.filter((s) => s.status === 'invalid').length
          const unknown = signReport.signatures.filter((s) => s.status === 'unknown').length
          const level = signReport.changedSince || bad ? 'warn' : unknown ? 'unknown' : 'ok'
          const text = signReport.changedSince ? 'Signed \u2014 changed since'
            : bad ? `${bad} signature problem${bad === 1 ? '' : 's'}`
              : unknown ? 'Signed (signer not verified)' : 'Signed and valid'
          return (
            <button className="statusbar__signed" data-level={level} onClick={() => setSignaturesOpen(true)}
                    title="Show the signatures">
              {level === 'ok' ? '\u2714' : '\u26A0'} {text}
            </button>
          )
        })()}
        {tool === 'sigbox' && (
          <span className="statusbar__tool">Drag a box where the signature should appear</span>
        )}
        {tool.startsWith('measure-') && (
          <span className="statusbar__tool">
            {(() => {
              const s = (scales[current] ?? [])[0]
              const how = tool === 'measure-distance' ? 'Drag to measure' : 'Click each corner; double-click to finish'
              return s ? `${how} \u00B7 scale ${s.ratio}` : `${how} \u00B7 no scale set (Tools \u203a Scale)`
            })()}
          </span>
        )}
        {tool === 'calibrate' && (
          <span className="statusbar__tool">Drag along a dimension whose real length you know</span>
        )}
        {tool.startsWith('field-') && (
          <span className="statusbar__tool">Drag a box for the new field</span>
        )}
        {session?.protected && (
          <span className="statusbar__protected"
                title={session.locked
                  ? 'Restricted: some changes are not allowed. Review \u203a Protect to unlock.'
                  : 'Saved with password protection'}>
            {'\u{1F512}'} {session.locked ? 'Restricted' : 'Protected'}
          </span>
        )}
        {session?.redacted && (
          <span className="statusbar__redacted" title="Save will flatten and verify this file">
            Redacted — save will flatten
          </span>
        )}
        {(tool === 'note' || tool === 'highlight') && (
          <span className="statusbar__tool">
            {tool === 'note'
              ? 'Click the page to place a note'
              : 'Drag over text to highlight it'}
          </span>
        )}
        {tool === 'crop' && (
          <span
            className="statusbar__tool"
            data-danger={cropDestructive || undefined}
          >
            {cropDestructive
              ? 'Drag to crop — content outside will be destroyed'
              : 'Drag to crop — trimmed content stays in the file'}
          </span>
        )}
        {tool === 'redact' ? (
          <span className="statusbar__tool">Drag over content to destroy it</span>
        ) : tool === 'whiteout' ? (
          <span className="statusbar__tool">Drag a box to white it out, then type over it</span>
        ) : (
          tool !== 'select' && (
            <span className="statusbar__tool">Click the page to place</span>
          )
        )}
        <span>{Math.round(scale * 100)}%</span>
        {busy && <span>{'Working…'}</span>}
      </footer>

      {notice && (
        <div className="toast" data-level={notice.level} role="status">
          <div className="toast__title">{notice.title}</div>
          <div className="toast__body">{notice.body}</div>
        </div>
      )}

      {scaleDialog && session && (
        <ScaleDialog
          pageIndex={scaleDialog.pageIndex}
          pageCount={session.pageCount}
          current={scales[scaleDialog.pageIndex] ?? []}
          calibration={scaleDialog.calibration}
          onRatio={(paper, paperUnit, real, realUnit, all) => void saveScale(
            { pages: all ? [] : [scaleDialog.pageIndex], paper, paperUnit, real, realUnit },
            `${paper} ${paperUnit} = ${real} ${realUnit}${all ? ' on every page' : ''}.`)}
          onCalibrate={(points, real, realUnit, all) => void saveScale(
            { pages: all ? [] : [scaleDialog.pageIndex], calibratePoints: points, real, realUnit },
            `Calibrated: that line is ${real} ${realUnit}.`)}
          onStartCalibrating={() => {
            setScaleDialog(null)
            setTool('calibrate')
            say('info', 'Calibrate', 'Drag along a dimension whose real length you know.')
          }}
          onRemove={(all) => void saveScale({ pages: all ? [] : [scaleDialog.pageIndex], remove: true }, 'Scale removed.')}
          onClose={() => setScaleDialog(null)}
        />
      )}

      {pendingLeave && session && (
        <SaveChangesDialog
          filename={session.filename}
          then={pendingLeave.then}
          signatures={signReport?.changedSince ? signReport.signatures.length : 0}
          busy={savingForLeave}
          onCancel={() => setPendingLeave(null)}
          onDiscard={() => {
            const next = pendingLeave.proceed
            setPendingLeave(null)
            next()
          }}
          onSave={() => {
            const next = pendingLeave.proceed
            setSavingForLeave(true)
            void handleSave().then((ok) => {
              setSavingForLeave(false)
              if (!ok) return             // stay: the file is not saved
              setPendingLeave(null)
              next()
            })
          }}
        />
      )}

      {editParagraph && (
        <RewrapDialog
          paragraph={editParagraph.paragraph}
          busy={rewrapping}
          onApply={(text) => void applyRewrap(text)}
          onCancel={() => setEditParagraph(null)}
        />
      )}

      {signOpen && session && (
        <CertificateSignDialog
          sessionId={session.id}
          emptyFields={signReport?.emptyFields ?? []}
          box={sigBox}
          hidden={tool === 'sigbox'}
          onDrawBox={() => setTool('sigbox')}
          onSigned={(r) => void signed(r)}
          onClose={() => { setSignOpen(false); setSigBox(null); if (tool === 'sigbox') setTool('select') }}
        />
      )}

      {signaturesOpen && signReport && (
        <SignaturesDialog
          report={signReport}
          onGoTo={(index) => { setSignaturesOpen(false); goTo(index) }}
          onClose={() => setSignaturesOpen(false)}
        />
      )}

      {comparePassword && (
        <PasswordDialog
          filename={comparePassword.name}
          wrong={comparePassword.wrong}
          action="Compare"
          onSubmit={(password) => void runCompare(comparePassword.blob, comparePassword.name, password)}
          onCancel={() => setComparePassword(null)}
        />
      )}

      {exportOpen && session && doc && (
        <ExportImagesDialog
          doc={doc}
          current={current}
          selected={[...selectedPages]}
          stem={stem()}
          onClose={() => setExportOpen(false)}
          onExported={(result) => {
            setExportOpen(false)
            saveBlob(result.blob, result.name)
            say('info', 'Exported', `${result.pages === 1 ? 'The page was' : `${result.pages} pages were`} saved as JPG` +
              (result.reduced.length
                ? `; page ${result.reduced.map((i) => i + 1).join(', ')} at a lower resolution, being so large.`
                : '.'))
          }}
        />
      )}

      {compressOpen && session && (
        <CompressDialog
          sessionId={session.id}
          onDone={(info) => void reload(info)}
          onClose={() => setCompressOpen(false)}
        />
      )}

      {linkArea && session && (
        <LinkDialog
          pageCount={session.pageCount}
          current={linkArea.pageIndex}
          onSubmit={(target) => void addLink(target)}
          onCancel={() => setLinkArea(null)}
        />
      )}

      {oneDriveMode && (
        <OneDriveDialog
          mode={oneDriveMode === 'compare' ? 'open' : oneDriveMode}
          convertible={oneDriveMode === 'open'}
          filename={session?.filename}
          onOpen={(item) => {
            if (oneDriveMode === 'compare') {
              setOneDriveMode(null)
              void run('Could not fetch from OneDrive', () => oneDrive.download(item))
                .then((r) => r && runCompare(r.blob, r.item.name))
            } else {
              guardUnsaved('open', () => void openFromOneDrive(item))
            }
          }}
          onSave={saveToOneDrive}
          onClose={() => setOneDriveMode(null)}
        />
      )}

      {passwordFor && (
        <PasswordDialog
          filename={passwordFor.file.name}
          wrong={passwordFor.wrong}
          onSubmit={(password) => void openFile(passwordFor.file, password)}
          onCancel={() => setPasswordFor(null)}
        />
      )}

      {mergePassword && (
        <PasswordDialog
          filename={mergePassword.files[mergePassword.index].name}
          wrong={mergePassword.wrong}
          action="Combine"
          permissionsOnly={mergePassword.permissions}
          onSubmit={(password) => {
            const passwords = mergePassword.files.map((_, i) => mergePassword.passwords[i] ?? '')
            passwords[mergePassword.index] = password
            void mergeFiles(mergePassword.files, passwords)
          }}
          onCancel={() => setMergePassword(null)}
        />
      )}

      {protectOpen && session && (
        <ProtectDialog
          sessionId={session.id}
          onClose={() => setProtectOpen(false)}
          onChanged={(info, message) => {
            setSession(info)
            if (message) say('info', 'Protect', message)
          }}
        />
      )}

      {marksOpen && session && (
        <MarksDialog
          sessionId={session.id}
          onClose={() => setMarksOpen(false)}
          onSaved={(info) => void reload(info)}
        />
      )}

      {stampAt && (
        <StampDialog
          author={currentAccount()?.name ?? ''}
          onPlace={(stamp, detail) => void placeStamp(stamp, detail)}
          onCancel={() => setStampAt(null)}
        />
      )}

      <SplitDialog
        open={splitOpen}
        sessionId={session?.id ?? null}
        pageCount={session?.pageCount ?? 0}
        onCancel={() => setSplitOpen(false)}
        onDownloaded={(count) => {
          setSplitOpen(false)
          say('info', 'Split downloaded', `${count} file(s) in the ZIP.`)
        }}
      />

      <RedactionPanel
        open={redactPanelOpen}
        regions={foundRegions}
        onCancel={() => {
          setRedactPanelOpen(false)
          setFoundRegions([])
        }}
        onApply={(regions) => void applyRedactions(regions)}
      />

      <SignatureDialog
        open={signatureOpen}
        onCancel={() => {
          setSignatureOpen(false)
          setPendingPlace(null)
          setTool('select')
        }}
        onConfirm={(result) => void handleSignature(result)}
      />

      {/* Hidden inputs: native pickers for create, combine and image placement. */}
      <input
        ref={createInput}
        type="file"
        accept={CREATE_ACCEPT}
        multiple
        hidden
        onChange={(e) => {
          const files = [...(e.target.files ?? [])]
          e.target.value = ''
          if (files.length) guardUnsaved('open', () => void createFromFiles(files))
        }}
      />
      <input
        ref={mergeInput}
        type="file"
        accept="application/pdf,.pdf"
        multiple
        hidden
        onChange={(e) => {
          const files = [...(e.target.files ?? [])]
          e.target.value = ''
          void mergeFiles(files)
        }}
      />
      <input
        ref={imageInput}
        type="file"
        accept="image/png,image/jpeg"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0]
          e.target.value = ''
          if (file && pendingPlace) {
            void placeImage(file, pendingPlace.pageIndex, pendingPlace.x, pendingPlace.y, 'image')
            setPendingPlace(null)
          }
        }}
      />
    </div>
  )
}

interface TabDeps {
  session: SessionInfo | null
  tool: Tool
  setTool: (tool: Tool) => void
  onOpen: () => void
  onSave: () => void
  onSaveAs: () => void
  onClose: () => void
  onCertificateSign: () => void
  onShowSignatures: () => void
  hasSignatures: boolean
  onCompareFile: () => void
  onCompareOneDrive: () => void
  askOpen: boolean
  onToggleAsk: () => void
  onScale: () => void
  onDetectFields: () => void
  accessOpen: boolean
  onToggleAccess: () => void
  oneDrive: boolean
  onOneDriveOpen: () => void
  onOneDriveSave: () => void
  onCompress: () => void
  onExportJpg: () => void
  onCreatePdf: () => void
  onFlatten: () => void
  hasForm: boolean
  highlightFields: boolean
  onToggleHighlight: () => void
  onClearForm: () => void
  onLockForm: () => void
  bookmarksOpen: boolean
  onToggleBookmarks: () => void
  onCombine: () => void
  onUndo: () => void
  onRedo: () => void
  onDeletePages: () => void
  onRotate: () => void
  onOcr: () => void
  onScanSensitive: () => void
  onSplit: () => void
  onCropContent: () => void
  cropDestructive: boolean
  toggleCropMode: () => void
  onRotateCcw: () => void
  onRotate180: () => void
  onDuplicate: () => void
  onInsertBlank: () => void
  onExtractImages: () => void
  onExtractText: () => void
  onExtractJson: () => void
  onToggleComments: () => void
  commentsOpen: boolean
  hasSelectedObject: boolean
  onArrange: (action: ArrangeAction) => void
  onZoomIn: () => void
  onZoomOut: () => void
  onFit: () => void
  onFind: () => void
  onReplace: () => void
  onMarks: () => void
  onProtect: () => void
}

function buildTabs(d: TabDeps): Tab[] {
  const open = Boolean(d.session)
  const toolBtn = (id: Tool, label: string, icon: string) => ({
    id,
    label,
    icon,
    onClick: () => d.setTool(d.tool === id ? 'select' : id),
    disabled: !open,
    active: d.tool === id,
  })

  return [
    {
      id: 'home',
      label: 'Home',
      groups: [
        {
          label: 'File',
          commands: [
            { id: 'open', label: 'Open', icon: '\u{1F4C2}', onClick: d.onOpen },
            {
              id: 'create', label: 'Create PDF', icon: '\u2795', onClick: d.onCreatePdf,
              hint: 'Make a PDF from pictures or Word, Excel and PowerPoint files (several become one PDF)',
            },
            { id: 'save', label: 'Save', icon: '\u{1F4BE}', onClick: d.onSave, disabled: !open },
            { id: 'saveas', label: 'Save As', icon: '\u{1F4D1}', onClick: d.onSaveAs, disabled: !open },
            {
              id: 'close', label: 'Close', icon: '\u2716', small: true, onClick: d.onClose, disabled: !open,
              hint: 'Close the document (asks to save changes first)',
            },
            {
              id: 'onedrive', label: 'OneDrive', icon: '☁', onClick: d.onOneDriveOpen,
              disabled: !d.oneDrive,
              hint: d.oneDrive ? 'Open from OneDrive or SharePoint'
                : 'OneDrive needs sign-in, which is off on this server',
            },
            {
              id: 'savecloud', label: 'Save to OneDrive', icon: '\u2601', small: true,
              onClick: d.onOneDriveSave, disabled: !open || !d.oneDrive,
              hint: 'Save a copy to OneDrive or SharePoint',
            },
            {
              id: 'compress', label: 'Reduce size', icon: '\u{1F5DC}', small: true,
              onClick: d.onCompress, disabled: !open, hint: 'Reduce file size',
            },
            {
              id: 'exportjpg', label: 'Export JPG', icon: '\u{1F4F7}', small: true,
              onClick: d.onExportJpg, disabled: !open, hint: 'Save pages as JPEG images',
            },
          ],
        },
        {
          label: 'Edit',
          commands: [
            { id: 'undo', label: 'Undo', icon: '↶', onClick: d.onUndo, disabled: !d.session?.canUndo },
            { id: 'redo', label: 'Redo', icon: '↷', onClick: d.onRedo, disabled: !d.session?.canRedo },
            toolBtn('paragraph', 'Paragraph', '¶'),
            {
              ...toolBtn('whiteout', 'Whiteout', '\u25AF'),
              hint: 'Draw a box: everything in it is replaced with white, then type over it',
            },
            { id: 'find', label: 'Find', icon: '\u{1F50D}', onClick: d.onFind, disabled: !open, small: true },
            { id: 'replace', label: 'Replace', icon: '\u21C4', onClick: d.onReplace, disabled: !open, small: true },
            {
              id: 'flatten', label: 'Flatten', icon: '\u25A4', onClick: d.onFlatten, disabled: !open, small: true,
              hint: 'Make text boxes, images, signatures, form answers and comments part of the page',
            },
          ],
        },
        {
          label: 'Insert',
          commands: [
            toolBtn('text', 'Text', '\u{1D413}'),
            { ...toolBtn('date', 'Date', '\u{1F4C5}'), hint: "Click to add today's date" },
            toolBtn('image', 'Image', '\u{1F5BC}'),
            toolBtn('signature', 'Signature', '✍'),
            { ...toolBtn('link', 'Link', '\u{1F517}'), hint: 'Draw a link; existing links show for removal' },
          ],
        },
        {
          label: 'Arrange',
          commands: [
            {
              id: 'front', label: 'Front', icon: '⤒',
              onClick: () => d.onArrange('front'),
              disabled: !d.hasSelectedObject,
            },
            {
              id: 'back', label: 'Back', icon: '⤓',
              onClick: () => d.onArrange('back'),
              disabled: !d.hasSelectedObject,
            },
            {
              id: 'fwd', label: 'Forward', icon: '↑',
              onClick: () => d.onArrange('forward'),
              disabled: !d.hasSelectedObject, small: true,
            },
            {
              id: 'bwd', label: 'Backward', icon: '↓',
              onClick: () => d.onArrange('backward'),
              disabled: !d.hasSelectedObject, small: true,
            },
          ],
        },
      ],
    },
    {
      id: 'pages',
      label: 'Pages',
      groups: [
        {
          label: 'Organise',
          commands: [
            { id: 'combine', label: 'Combine', icon: '⧉', onClick: d.onCombine, disabled: !open },
            { id: 'split', label: 'Split', icon: '✂', onClick: d.onSplit, disabled: !open },
            { id: 'dup', label: 'Duplicate', icon: '⧈', onClick: d.onDuplicate, disabled: !open },
            { id: 'blank', label: 'Blank', icon: '▢', onClick: d.onInsertBlank, disabled: !open },
            { id: 'delpage', label: 'Delete', icon: '✖', onClick: d.onDeletePages, disabled: !open },
          ],
        },
        {
          label: 'Rotate',
          commands: [
            { id: 'rotcw', label: 'Right', icon: '↻', onClick: d.onRotate, disabled: !open },
            { id: 'rotccw', label: 'Left', icon: '↺', onClick: d.onRotateCcw, disabled: !open },
            { id: 'rot180', label: '180°', icon: '⇅', onClick: d.onRotate180, disabled: !open },
          ],
        },
        {
          label: 'Crop',
          commands: [
            toolBtn('crop', 'Draw', '⌗'),
            { id: 'cropfit', label: 'To content', icon: '⤢', onClick: d.onCropContent, disabled: !open },
            {
              id: 'cropmode',
              label: d.cropDestructive ? 'Remove' : 'Hide',
              icon: d.cropDestructive ? '⚠' : '👁',
              onClick: d.toggleCropMode,
              disabled: !open,
              active: d.cropDestructive,
              small: true,
            },
          ],
        },
        {
          label: 'Page marks',
          commands: [
            {
              id: 'marks', label: 'Header & Footer', icon: '\u2261',
              onClick: d.onMarks, disabled: !open,
            },
            {
              id: 'watermark', label: 'Watermark', icon: '\u{1F4A7}',
              onClick: d.onMarks, disabled: !open,
            },
          ],
        },
        {
          label: 'Recognise',
          commands: [
            { id: 'ocr', label: 'OCR', icon: '\u{1F50E}', onClick: d.onOcr, disabled: !open },
          ],
        },
      ],
    },
    {
      id: 'review',
      label: 'Review',
      groups: [
        {
          label: 'Redact',
          commands: [
            toolBtn('redact', 'Draw', '▓'),
            {
              id: 'scan',
              label: 'Find PII',
              icon: '\u{1F50D}',
              onClick: d.onScanSensitive,
              disabled: !open,
              small: true,
            },
          ],
        },
        {
          label: 'Comments',
          commands: [
            toolBtn('note', 'Note', '\u{1F4AC}'),
            toolBtn('highlight', 'Highlight', '\u{1F58D}'),
            { ...toolBtn('underline', 'Underline', 'U\u0332'), small: true },
            { ...toolBtn('strikeout', 'Strike', 'S\u0336'), small: true },
            toolBtn('stamp', 'Stamp', '\u{1F3F7}'),
            {
              id: 'panel',
              label: 'Panel',
              icon: '\u2263',
              onClick: d.onToggleComments,
              disabled: !open,
              active: d.commentsOpen,
              small: true,
            },
          ],
        },
        {
          label: 'Draw',
          commands: [
            toolBtn('pen', 'Pen', '\u270E'),
            { ...toolBtn('rectangle', 'Rectangle', '\u25AD'), small: true },
            { ...toolBtn('ellipse', 'Ellipse', '\u25EF'), small: true },
            { ...toolBtn('line', 'Line', '\u2571'), small: true },
            { ...toolBtn('arrow', 'Arrow', '\u2197'), small: true },
          ],
        },
        {
          label: 'Sign',
          commands: [
            {
              id: 'certsign', label: 'Certificate', icon: '\u{1F58B}', onClick: d.onCertificateSign,
              disabled: !open, hint: 'Sign with a certificate (digital ID)',
            },
            {
              id: 'signatures', label: 'Signatures', icon: '\u2714', small: true, onClick: d.onShowSignatures,
              disabled: !open || !d.hasSignatures, hint: 'Check the document\u2019s signatures',
            },
          ],
        },
        {
          label: 'Compare',
          commands: [
            {
              id: 'compare', label: 'Compare', icon: '\u2194', onClick: d.onCompareFile, disabled: !open,
              hint: 'Compare with another version of this file, from this computer',
            },
            {
              id: 'comparecloud', label: 'From OneDrive', icon: '\u2601', small: true, onClick: d.onCompareOneDrive,
              disabled: !open || !d.oneDrive, hint: 'Compare with a version on OneDrive or SharePoint',
            },
          ],
        },
        {
          label: 'Swifty',
          commands: [
            {
              id: 'ask', label: 'Ask Swifty', icon: '\u{1F4AC}', onClick: d.onToggleAsk, disabled: !open,
              active: d.askOpen, hint: 'Ask questions about this document, or summarise it',
            },
          ],
        },
        {
          label: 'Protect',
          commands: [
            {
              id: 'protect', label: 'Protect', icon: '\u{1F512}',
              onClick: d.onProtect, disabled: !open,
              active: Boolean(d.session?.protected),
            },
          ],
        },
        {
          label: 'Extract',
          commands: [
            { id: 'eximg', label: 'Images', icon: '\u{1F5BC}', onClick: d.onExtractImages, disabled: !open, small: true },
            { id: 'extxt', label: 'Text', icon: '\u{1F4C4}', onClick: d.onExtractText, disabled: !open, small: true },
            { id: 'exjson', label: 'JSON', icon: '{ }', onClick: d.onExtractJson, disabled: !open, small: true },
          ],
        },
      ],
    },
    {
      id: 'forms',
      label: 'Forms',
      groups: [
        {
          label: 'Fill',
          commands: [
            {
              id: 'fill', label: 'Highlight', icon: '\u{1F4DD}', onClick: d.onToggleHighlight,
              disabled: !d.hasForm, active: d.hasForm && d.highlightFields,
              hint: d.hasForm ? 'Tint the fillable fields. Click a field on the page to fill it in.'
                : 'This document has no form fields',
            },
            { id: 'clearform', label: 'Clear', icon: '\u232B', small: true, onClick: d.onClearForm, disabled: !d.hasForm },
            {
              id: 'lockform', label: 'Lock answers', icon: '\u{1F510}', small: true, onClick: d.onLockForm,
              disabled: !d.hasForm, hint: 'Make the answers part of the page, so they can no longer be changed',
            },
          ],
        },
        {
          label: 'Prepare',
          commands: [
            { ...toolBtn('prepare', 'Edit fields', '\u{1F4CB}'), hint: 'Select, move and change the form\u2019s fields' },
            { ...toolBtn('field-text', 'Text', '\u{1F524}'), small: true, hint: 'Add a text field' },
            { ...toolBtn('field-checkbox', 'Checkbox', '\u2611'), small: true, hint: 'Add a checkbox' },
            { ...toolBtn('field-radio', 'Radio', '\u25C9'), small: true, hint: 'Add a radio button (to the selected group, if one is)' },
            { ...toolBtn('field-combo', 'Dropdown', '\u25BE'), small: true, hint: 'Add a dropdown' },
            { ...toolBtn('field-list', 'List', '\u2630'), small: true, hint: 'Add a list box' },
            { ...toolBtn('field-signature', 'Signature', '\u270D'), small: true, hint: 'Add a signature field' },
            { id: 'detect', label: 'Detect', icon: '\u{1F50E}', onClick: d.onDetectFields, disabled: !open,
              hint: 'Find the blanks, boxes and lines on this page that want fields' },
          ],
        },
      ],
    },
    {
      id: 'tools',
      label: 'Tools',
      groups: [
        {
          label: 'Measure',
          commands: [
            { ...toolBtn('measure-distance', 'Distance', '\u{1F4CF}'), hint: 'Measure a distance at the drawing\u2019s scale' },
            { ...toolBtn('measure-perimeter', 'Perimeter', '\u2B20'), small: true, hint: 'Measure a perimeter: click each corner' },
            { ...toolBtn('measure-area', 'Area', '\u25A7'), small: true, hint: 'Measure an area: click each corner' },
            { id: 'scale', label: 'Scale', icon: '\u2696', small: true, onClick: d.onScale, disabled: !open,
              hint: 'Set or calibrate the scale' },
          ],
        },
        {
          label: 'Accessibility',
          commands: [
            { id: 'access', label: 'Accessibility', icon: '\u267F', onClick: d.onToggleAccess, disabled: !open,
              active: d.accessOpen, hint: 'Check accessibility, add tags, set the reading order and alt text' },
          ],
        },
      ],
    },
    {
      id: 'view',
      label: 'View',
      groups: [
        {
          label: 'Zoom',
          commands: [
            { id: 'zoomin', label: 'Zoom In', icon: '➕', onClick: d.onZoomIn },
            { id: 'zoomout', label: 'Zoom Out', icon: '➖', onClick: d.onZoomOut },
            { id: 'fit', label: 'Fit', icon: '⤢', onClick: d.onFit },
          ],
        },
        {
          label: 'Panels',
          commands: [
            {
              id: 'bookmarks', label: 'Bookmarks', icon: '\u{1F516}', onClick: d.onToggleBookmarks,
              disabled: !open, active: d.bookmarksOpen,
            },
          ],
        },
      ],
    },
  ]
}

/** Pages | Bookmarks, at the top of the left panel. */
function SideTabs({ panel, onPanel, bookmarks }: {
  panel: 'pages' | 'bookmarks'
  onPanel: (panel: 'pages' | 'bookmarks') => void
  bookmarks: number
}) {
  return (
    <div className="sidetabs" role="tablist">
      <button role="tab" aria-selected={panel === 'pages'} className="sidetab"
              onClick={() => onPanel('pages')}>Pages</button>
      <button role="tab" aria-selected={panel === 'bookmarks'} className="sidetab"
              onClick={() => onPanel('bookmarks')}>
        Bookmarks{bookmarks ? ` (${bookmarks})` : ''}
      </button>
    </div>
  )
}
